import { useState, useMemo, useRef, useEffect } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { safeFormat } from "@/lib/dateUtils";
import { formatCurrencyInput, parseCurrencyInput } from "@/lib/inputFormatters";
import { showOperationSuccess } from "@/lib/operationFeedback";
import { getQueuePanelProfile } from "@shared/queuePanelProfiles";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { EmptyState } from "@/components/EmptyState";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  CreditCard,
  Banknote,
  Smartphone,
  Wallet,
  Gift,
  MoreHorizontal,
  CheckCircle2,
  Clock,
  User,
  Scissors,
  DollarSign,
  Phone,
  Mail,
  TrendingUp,
  Calendar,
  MessageCircle,
  Loader2,
  RotateCcw,
  Plus,
  Trash2,
  Percent,
  Printer,
  ShieldAlert,
  Users,
  FileText,
  Receipt,
  Download,
  Filter,
  MapPin,
  Package,
  Search,
  FlaskConical,
  ArrowUp,
  ArrowDown,
  ArrowUpCircle,
  ArrowDownCircle,
  AlertCircle,
} from "lucide-react";
import { AuthPinModal } from "@/components/AuthPinModal";
import { getNextMobilePendingPayment, isMobileSaleActionDisabled } from "@shared/mobileOperations";
import { MobileSaleActionBar } from "@/components/MobileOperationControls";
import { format, startOfDay, endOfDay, startOfMonth, endOfMonth, startOfWeek, endOfWeek } from "date-fns";
import { ptBR } from "date-fns/locale";

type PaymentMethod = "dinheiro" | "pix" | "credito" | "debito" | "voucher" | "outro";

const paymentMethods: { value: PaymentMethod; label: string; icon: React.ReactNode; color: string }[] = [
  { value: "dinheiro", label: "Dinheiro", icon: <Banknote className="w-5 h-5" />, color: "bg-green-100 text-green-700 border-green-200 hover:bg-green-200" },
  { value: "pix", label: "PIX", icon: <Smartphone className="w-5 h-5" />, color: "bg-teal-100 text-teal-700 border-teal-200 hover:bg-teal-200" },
  { value: "credito", label: "Crédito", icon: <CreditCard className="w-5 h-5" />, color: "bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-200" },
  { value: "debito", label: "Débito", icon: <Wallet className="w-5 h-5" />, color: "bg-purple-100 text-purple-700 border-purple-200 hover:bg-purple-200" },
  { value: "voucher", label: "Voucher", icon: <Gift className="w-5 h-5" />, color: "bg-orange-100 text-orange-700 border-orange-200 hover:bg-orange-200" },
  { value: "outro", label: "Outro", icon: <MoreHorizontal className="w-5 h-5" />, color: "bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200" },
];

const methodColors: Record<string, string> = {
  dinheiro: "bg-green-100 text-green-700",
  pix: "bg-teal-100 text-teal-700",
  credito: "bg-blue-100 text-blue-700",
  debito: "bg-purple-100 text-purple-700",
  voucher: "bg-orange-100 text-orange-700",
  outro: "bg-gray-100 text-gray-700",
};

const methodLabels: Record<string, string> = {
  dinheiro: "Dinheiro", pix: "PIX", credito: "Crédito", debito: "Débito", voucher: "Voucher", outro: "Outro",
};

function getCashAmountFromPayment(payment: any): number {
  if (payment.splits?.length) {
    return payment.splits
      .filter((split: any) => split.paymentMethod === "dinheiro")
      .reduce((sum: number, split: any) => sum + Number(split.amount ?? 0), 0);
  }
  return payment.paymentMethod === "dinheiro"
    ? Number(payment.totalPrice ?? payment.amount ?? 0)
    : 0;
}

function calculateCashReceivedForPhysicalCount(payments: any[]): number {
  return payments
    .filter((payment) => !payment.isLateCashEntry)
    .reduce((sum, payment) => sum + getCashAmountFromPayment(payment), 0);
}

type PeriodPreset = "today" | "week" | "month" | "custom";
type ReportView = "all" | "discounts" | "station";

// ─── Exportar Relatório em PDF ────────────────────────────────────────────────
function exportReportPDF(
  report: any,
  period: string,
  salonName: string = "Salão",
  manualMovements: Array<any> = [],
) {
  const win = window.open("", "_blank", "width=800,height=900");
  if (!win) return;
  const now = new Date();

  const methodLbls: Record<string, string> = {
    dinheiro: "Dinheiro", pix: "PIX", credito: "Crédito",
    debito: "Débito", voucher: "Voucher", outro: "Outro",
  };

  const totalsRows = report.totalByMethod
    .map((m: any) =>
      `<tr><td>${methodLbls[m.paymentMethod] ?? m.paymentMethod ?? "Não informado"}</td>
       <td style="text-align:right">${m.count}x</td>
       <td style="text-align:right">R$ ${Number(m.total).toFixed(2)}</td></tr>`
    ).join("");

  const detailRows = report.payments.map((p: any) => {
    const splitsText = p.splits && p.splits.length > 0
      ? p.splits.map((s: any) =>
          `${methodLbls[s.paymentMethod] ?? s.paymentMethod}: R$ ${Number(s.amount).toFixed(2)}`
        ).join(" | ")
      : methodLbls[p.paymentMethod] ?? p.paymentMethod ?? "—";
    const lateEntryText = p.isLateCashEntry
      ? `<br/><small style="color:#9a6700">Atendimento em ${format(new Date(p.startTime), "dd/MM/yyyy HH:mm")} · lançado no caixa em ${format(new Date(p.cashEntryAt), "dd/MM/yyyy HH:mm")}</small>`
      : "";
    const discountText = Number(p.discountAmount) > 0
      ? `<br/><small style="color:#c00">Desconto: R$ ${Number(p.discountAmount).toFixed(2)}${p.discountReason ? ` (${p.discountReason})` : ""}</small>`
      : "";
    const statusText = p.paymentStatus === "refunded"
      ? `<span style="color:#c00;font-weight:bold"> [ESTORNADO]</span>` : "";
    return `<tr>
      <td>${format(new Date(p.startTime), "dd/MM/yyyy HH:mm")}${lateEntryText}</td>
      <td>${p.clientName ?? "Cliente"}${statusText}</td>
      <td>${p.serviceName ?? "Serviço"}</td>
      <td>${splitsText}${discountText}</td>
      <td style="text-align:right;font-weight:bold">R$ ${Number(p.totalPrice).toFixed(2)}</td>
    </tr>`;
  }).join("");

  const discountPayments = report.payments.filter((p: any) => Number(p.discountAmount) > 0);
  const totalDiscounts = discountPayments.reduce((s: number, p: any) => s + Number(p.discountAmount), 0);
  const discountSection = discountPayments.length > 0 ? `
    <h3 style="margin-top:24px;color:#c00">Descontos Aplicados (${discountPayments.length})</h3>
    <table>
      <thead><tr><th>Data</th><th>Cliente</th><th>Serviço</th><th>Motivo</th><th style="text-align:right">Desconto</th></tr></thead>
      <tbody>
        ${discountPayments.map((p: any) => `<tr>
          <td>${format(new Date(p.startTime), "dd/MM/yyyy HH:mm")}</td>
          <td>${p.clientName ?? "Cliente"}</td>
          <td>${p.serviceName ?? "Serviço"}</td>
          <td>${p.discountReason ?? "—"}</td>
          <td style="text-align:right;color:#c00">- R$ ${Number(p.discountAmount).toFixed(2)}</td>
        </tr>`).join("")}
        <tr style="font-weight:bold;border-top:2px solid #000">
          <td colspan="4">Total de descontos</td>
          <td style="text-align:right;color:#c00">- R$ ${totalDiscounts.toFixed(2)}</td>
        </tr>
      </tbody>
    </table>` : "";
  const manualEntries = manualMovements.filter((movement) => movement.direction === "income");
  const manualExpenses = manualMovements.filter((movement) => movement.direction !== "income");
  const manualEntryTotal = manualEntries.reduce((sum, movement) => sum + Number(movement.amount ?? 0), 0);
  const manualExpenseTotal = manualExpenses.reduce((sum, movement) => sum + Number(movement.amount ?? 0), 0);
  const movementRows = manualMovements.map((movement) => {
    const isIncome = movement.direction === "income";
    return `<tr><td>${format(new Date(movement.createdAt), "dd/MM/yyyy HH:mm")}</td><td>${isIncome ? "Entrada" : "Saída"}</td><td>${escapeCashReceiptHtml(methodLbls[movement.paymentMethod] ?? movement.paymentMethod)}</td><td>${escapeCashReceiptHtml(movement.description)}${movement.notes ? `<br/><small>${escapeCashReceiptHtml(movement.notes)}</small>` : ""}</td><td style="text-align:right;font-weight:bold;color:${isIncome ? "#087f23" : "#c00"}">${isIncome ? "+" : "-"} R$ ${Number(movement.amount ?? 0).toFixed(2)}</td></tr>`;
  }).join("");
  const manualSection = manualMovements.length > 0 ? `
    <h2>Movimentações Manuais de Caixa</h2>
    <p class="meta">Entradas manuais: + R$ ${manualEntryTotal.toFixed(2)} · Saídas manuais: - R$ ${manualExpenseTotal.toFixed(2)}. Não compõem o faturamento ou as comissões.</p>
    <table><thead><tr><th>Data/Hora</th><th>Tipo</th><th>Forma</th><th>Descrição</th><th style="text-align:right">Valor</th></tr></thead><tbody>${movementRows}</tbody></table>` : "";
  const paymentCount = report.payments.length;
  const ticketAverage = paymentCount > 0 ? Number(report.grandTotal) / paymentCount : 0;

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8"/>
  <title>Relatório de Caixa — ${salonName}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: Arial, sans-serif; font-size: 11px; padding: 24px; color: #000; }
    h1 { font-size: 18px; margin-bottom: 4px; }
    h2 { font-size: 14px; margin: 16px 0 8px; border-bottom: 1px solid #ccc; padding-bottom: 4px; }
    h3 { font-size: 13px; margin: 16px 0 8px; }
    .header { border-bottom: 2px solid #000; padding-bottom: 12px; margin-bottom: 16px; }
    .meta { color: #555; font-size: 10px; margin-top: 4px; }
    .total-box { background: #f5f5f5; border: 1px solid #ddd; border-radius: 6px; padding: 12px; margin: 12px 0; }
    .total-box .big { font-size: 22px; font-weight: bold; color: #000; }
    table { width: 100%; border-collapse: collapse; margin-top: 8px; }
    th { background: #f0f0f0; text-align: left; padding: 6px 8px; font-size: 10px; border: 1px solid #ddd; }
    td { padding: 5px 8px; border: 1px solid #eee; vertical-align: top; }
    tr:nth-child(even) td { background: #fafafa; }
    .footer { margin-top: 24px; font-size: 9px; color: #888; border-top: 1px solid #ddd; padding-top: 8px; }
    @media print { body { padding: 12px; } }
  </style>
</head>
<body>
  <div class="header">
    <h1>${salonName}</h1>
    <p class="meta">Relatório de Caixa — Período: ${period}</p>
    <p class="meta">Gerado em: ${format(now, "dd/MM/yyyy 'às' HH:mm")}</p>
  </div>

  <div class="total-box">
    <p>Faturamento Total</p>
    <p class="big">R$ ${Number(report.grandTotal).toFixed(2)}</p>
    <p class="meta">${paymentCount > 0 ? `${paymentCount} pagamento(s) · Ticket médio: R$ ${ticketAverage.toFixed(2)}` : "Nenhum pagamento registrado no período"}</p>
  </div>

  <h2>Por Forma de Pagamento</h2>
  <table>
    <thead><tr><th>Método</th><th style="text-align:right">Qtd</th><th style="text-align:right">Total</th></tr></thead>
    <tbody>${totalsRows}</tbody>
  </table>

  <h2>Detalhamento dos Pagamentos</h2>
  <table>
    <thead><tr><th>Data/Hora</th><th>Cliente</th><th>Serviço</th><th>Pagamento</th><th style="text-align:right">Valor</th></tr></thead>
    <tbody>${detailRows}</tbody>
  </table>

  ${discountSection}
  ${manualSection}

  <div class="footer">
    <p>Documento gerado automaticamente pelo sistema de gestão do salão.</p>
  </div>
</body>
</html>`;

  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => { win.print(); win.close(); }, 600);
}

function escapeCashReceiptHtml(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

// A impressão usa somente a fotografia financeira guardada no fechamento.
// Assim, uma reimpressão não consulta nem recalcula entradas posteriores.
function printCashShiftClosing(input: {
  shift: any;
  salonName: string;
  totalsByMethod: Array<any>;
  paymentDetails: Array<any>;
  expenseDetails: Array<any>;
}): boolean {
  const win = window.open("", "_blank", "width=430,height=720");
  if (!win) return false;

  const { shift, salonName, totalsByMethod, paymentDetails, expenseDetails } = input;
  const money = (value: unknown) => `R$ ${Number(value ?? 0).toFixed(2).replace(".", ",")}`;
  const periodLabel = shift.periodType === "morning" ? "Manhã" : shift.periodType === "afternoon" ? "Tarde" : "Personalizado";
  const methodRows = totalsByMethod.length > 0
    ? totalsByMethod.map((item) => `<div class="row"><span>${escapeCashReceiptHtml(methodLabels[item.paymentMethod] ?? item.paymentMethod)}</span><strong>${money(item.total)}</strong></div>`).join("")
    : "<p class=\"muted\">Nenhum recebimento informado.</p>";
  const paymentRows = paymentDetails.length > 0
    ? paymentDetails.map((payment) => {
      const paymentMethods = payment.splits?.length
        ? payment.splits.map((split: any) => `${methodLabels[split.paymentMethod] ?? split.paymentMethod}: ${money(split.amount)}`).join(" · ")
        : (methodLabels[payment.paymentMethod] ?? payment.paymentMethod ?? "Forma não informada");
      const lateEntryText = payment.isLateCashEntry && payment.serviceDate
        ? ` · Atendimento em ${format(new Date(payment.serviceDate), "dd/MM HH:mm")} · lançado no caixa em ${format(new Date(payment.cashEntryAt ?? payment.occurredAt), "dd/MM HH:mm")}`
        : "";
      return `<div class="item"><strong>${escapeCashReceiptHtml(payment.clientName ?? "Cliente")} · ${escapeCashReceiptHtml(payment.serviceName ?? "Serviço")}</strong><div class="row"><span>${escapeCashReceiptHtml(format(new Date(payment.occurredAt), "dd/MM HH:mm"))} · ${escapeCashReceiptHtml(paymentMethods)}${escapeCashReceiptHtml(lateEntryText)}</span><strong>${money(payment.amount)}</strong></div></div>`;
    }).join("")
    : "<p class=\"muted\">Nenhum recebimento neste turno.</p>";
  const manualEntries = expenseDetails.filter((movement) => movement.direction === "income");
  const manualExpenses = expenseDetails.filter((movement) => movement.direction !== "income");
  const movementRows = (movements: Array<any>, direction: "income" | "expense") => movements.length > 0
    ? movements.map((movement) => `<div class="item"><strong>${escapeCashReceiptHtml(movement.description)}</strong><div class="row"><span>${escapeCashReceiptHtml(format(new Date(movement.createdAt), "dd/MM HH:mm"))} · ${escapeCashReceiptHtml(movement.paymentMethod === "dinheiro" ? direction === "income" ? "Entrada em dinheiro" : "Saída em dinheiro" : methodLabels[movement.paymentMethod] ?? movement.paymentMethod)}${movement.notes ? ` · ${escapeCashReceiptHtml(movement.notes)}` : ""}</span><strong>${direction === "income" ? "+" : "-"} ${money(movement.amount)}</strong></div></div>`).join("")
    : `<p class="muted">Nenhuma ${direction === "income" ? "entrada" : "saída"} manual neste turno.</p>`;
  const manualEntryTotal = manualEntries.reduce((total, movement) => total + Number(movement.amount ?? 0), 0);
  const lateEntries = paymentDetails.filter((payment) => payment.isLateCashEntry);
  const lateEntriesTotal = lateEntries.reduce((total, payment) => total + Number(payment.amount ?? 0), 0);
  const lateEntriesCashTotal = lateEntries.reduce((total, payment) => total + getCashAmountFromPayment(payment), 0);
  const lateEntriesNote = lateEntries.length > 0
    ? `<p class="warning">ATENÇÃO: ${lateEntries.length} lançamento(s) de atendimento em data diferente · ${money(lateEntriesTotal)}. Os valores compõem este caixa porque foram lançados neste período.${lateEntriesCashTotal > 0 ? ` Dinheiro já recebido em data anterior: ${money(lateEntriesCashTotal)}; não entra no saldo físico esperado deste turno.` : ""}</p>`
    : "";

  win.document.write(`<!DOCTYPE html>
<html><head><meta charset="UTF-8"/><title>Comprovante de fechamento #${escapeCashReceiptHtml(shift.id)}</title>
<style>
  * { box-sizing: border-box; } body { width: 76mm; margin: 0 auto; padding: 12px; color: #000; font: 11px/1.35 'Courier New', monospace; }
  .center { text-align: center; } h1 { font-size: 16px; margin: 0 0 4px; } h2 { font-size: 12px; margin: 14px 0 6px; border-top: 1px dashed #000; padding-top: 8px; }
  .row { display: flex; justify-content: space-between; gap: 8px; margin: 3px 0; } .row span { min-width: 0; } .item { border-top: 1px dotted #777; padding: 6px 0; }
  .summary { border: 1px solid #000; padding: 8px; margin-top: 10px; } .net { font-size: 14px; font-weight: 700; } .muted { color: #555; margin: 4px 0; } .warning { border: 1px dashed #9a6700; background: #fff7d6; padding: 6px; margin: 8px 0; font-weight: 700; }
  .footer { border-top: 1px dashed #000; margin-top: 14px; padding-top: 8px; text-align: center; font-size: 9px; } @media print { body { padding: 0; } }
</style></head><body>
  <div class="center"><h1>${escapeCashReceiptHtml(salonName)}</h1><strong>COMPROVANTE DE FECHAMENTO</strong><p>Fechamento #${escapeCashReceiptHtml(shift.id)} · ${escapeCashReceiptHtml(periodLabel)}</p></div>
  <div class="summary"><div class="row"><span>Período</span><strong>${escapeCashReceiptHtml(format(new Date(shift.periodStart), "dd/MM HH:mm"))} — ${escapeCashReceiptHtml(shift.periodEnd ? format(new Date(shift.periodEnd), "dd/MM HH:mm") : "-")}</strong></div><div class="row"><span>Responsável</span><strong>${escapeCashReceiptHtml(shift.closedByName ?? "Não informado")}</strong></div><div class="row"><span>Recebimentos de vendas</span><strong>${money(shift.grossTotal)}</strong></div><div class="row"><span>Entradas manuais</span><strong>+ ${money(manualEntryTotal)}</strong></div><div class="row"><span>Saídas manuais</span><strong>- ${money(shift.expenseTotal)}</strong></div><div class="row"><span>Dinheiro físico</span><strong>${money(shift.expectedCashTotal)}</strong></div><div class="row net"><span>VENDAS MENOS SAÍDAS</span><strong>${money(shift.netTotal)}</strong></div></div>
  ${lateEntriesNote}
  <h2>Recebimentos por forma</h2>${methodRows}
  <h2>Recebimentos lançados (${paymentDetails.length})</h2>${paymentRows}
  <h2>Entradas manuais (${manualEntries.length})</h2>${movementRows(manualEntries, "income")}
  <h2>Saídas manuais (${manualExpenses.length})</h2>${movementRows(manualExpenses, "expense")}
  ${shift.notes ? `<h2>Observação</h2><p>${escapeCashReceiptHtml(shift.notes)}</p>` : ""}
  <div class="footer"><p>Dados preservados do fechamento original.</p><p>Reimpresso em ${escapeCashReceiptHtml(format(new Date(), "dd/MM/yyyy HH:mm"))}</p></div>
</body></html>`);
  win.document.close();
  win.focus();
  setTimeout(() => { win.print(); win.close(); }, 500);
  return true;
}

interface PaymentSplitEntry {
  method: PaymentMethod;
  amount: string;
  installments?: number;
}

interface CheckoutReceiptSnapshot {
  appointment: any;
  splits: PaymentSplitEntry[];
  discount: number;
  subtotal: number;
  totalPaid: number;
  change: number;
  salonName: string;
  items: Array<{ name: string; amount: number }>;
}

interface StandaloneSaleReceiptSnapshot {
  salonName: string;
  clientName: string;
  products: Array<{ name: string; quantity: number; unitPrice: number }>;
  splits: PaymentSplitEntry[];
  discount: number;
  totalPaid: number;
  change: number;
}

// ─── Impressão de Comanda ─────────────────────────────────────────────────────
function printComanda(appt: any, salonName: string = "Salão", extraServices: Array<{ serviceName: string; price: string; professionalName?: string }> = []) {
  const win = window.open("", "_blank", "width=400,height=600");
  if (!win) return;
  const now = new Date();
  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8"/>
      <title>Comanda #${appt.id}</title>
      <style>
        @page { size: 80mm auto; margin: 2mm; }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        html, body { width: 76mm; min-height: 0; }
        body { font-family: Arial, Helvetica, sans-serif; font-size: 10.5pt; line-height: 1.22; padding: 0; color: #000; font-weight: 500; }
        .center { text-align: center; }
        .bold { font-weight: 700; }
        .line { border-top: 1px dashed #000; margin: 5px 0; }
        .row { display: flex; justify-content: space-between; gap: 8px; margin: 2px 0; }
        .row > :last-child { text-align: right; }
        h2 { font-size: 15pt; line-height: 1.05; margin-bottom: 2px; font-weight: 700; }
        .big { font-size: 13pt; font-weight: 700; }
        .footer { margin-top: 7px; font-size: 8.5pt; color: #000; }
        .client-name { font-weight: 700; font-size: 11pt; }
        .client-phone { font-size: 9pt; }
        @media print { html, body { width: 76mm; } }
      </style>
    </head>
    <body>
      <div class="center">
        <h2>${salonName}</h2>
        <p>COMANDA DE SERVIÇO</p>
        <p>Nº ${appt.id}</p>
      </div>
      <div class="line"></div>
      <div class="row"><span>Data/Hora:</span><span>${format(new Date(appt.startTime), "dd/MM/yyyy HH:mm")}</span></div>
      <p class="client-name">${appt.clientName ?? appt.userName ?? "Cliente"}</p>
      ${appt.clientPhone ? `<p class="client-phone">${appt.clientPhone}</p>` : ""}
      <div class="row"><span>Profissional:</span><span>${appt.professionalName ?? "-"}</span></div>
      <div class="line"></div>
      <p class="bold">Serviços:</p>
      ${extraServices.length > 0
        ? extraServices.map(s =>
            `<div class="row"><span>${s.serviceName}${s.professionalName ? ` <span style="font-size:10px;color:#555">(${s.professionalName})</span>` : ''}</span><span>R$ ${Number(s.price).toFixed(2)}</span></div>`
          ).join('')
        : `<div class="row"><span>${appt.serviceName ?? "Serviço"}</span><span>R$ ${Number(appt.totalPrice ?? 0).toFixed(2)}</span></div>`
      }
      <div class="line"></div>
      <div class="row big"><span>TOTAL:</span><span>R$ ${Number(appt.totalPrice ?? 0).toFixed(2)}</span></div>
      <div class="line"></div>
      <div class="center footer">
        <p>Impresso em ${format(now, "dd/MM/yyyy HH:mm")}</p>
        <p>Aguarde ser chamado(a)</p>
      </div>
    </body>
    </html>
  `;
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => { win.print(); win.close(); }, 500);
}

// ─── Impressão de Nota de Checkout ────────────────────────────────────────────
function printCheckoutNote(receipt: CheckoutReceiptSnapshot): boolean {
  const win = window.open("", "_blank", "width=400,height=520");
  if (!win) return false;
  const { appointment: appt, splits, discount, subtotal, totalPaid, change, salonName, items } = receipt;
  const now = new Date();
  const splitsHtml = splits.map(s =>
    `<div class="row"><span>${methodLabels[s.method]}:</span><span>R$ ${Number(s.amount).toFixed(2)}</span></div>`
  ).join("");
  const itemsHtml = items.map((item) =>
    `<div class="row"><span>${item.name}</span><span>R$ ${Number(item.amount).toFixed(2)}</span></div>`
  ).join("");
  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8"/>
      <title>Nota de Checkout #${appt.id}</title>
      <style>
        @page { size: 80mm auto; margin: 2mm; }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        html, body { width: 76mm; min-height: 0; }
        body { font-family: Arial, Helvetica, sans-serif; font-size: 10.5pt; line-height: 1.22; padding: 0; color: #000; font-weight: 500; }
        .center { text-align: center; }
        .bold { font-weight: 700; }
        .line { border-top: 1px dashed #000; margin: 5px 0; }
        .row { display: flex; justify-content: space-between; gap: 8px; margin: 2px 0; }
        .row > :last-child { text-align: right; }
        h2 { font-size: 15pt; line-height: 1.05; margin-bottom: 2px; font-weight: 700; }
        .big { font-size: 13pt; font-weight: 700; }
        .discount { color: #000; font-weight: 700; }
        .footer { margin-top: 7px; font-size: 8.5pt; color: #000; }
        @media print { html, body { width: 76mm; } }
      </style>
    </head>
    <body>
      <div class="center">
        <h2>${salonName}</h2>
        <p>CUPOM DE FECHAMENTO</p>
        <p>Nº ${appt.id}</p>
      </div>
      <div class="line"></div>
      <div class="row"><span>Data/Hora:</span><span>${format(new Date(appt.startTime), "dd/MM/yyyy HH:mm")}</span></div>
      <div class="row"><span>Cliente:</span><span class="bold">${appt.clientName ?? appt.userName ?? "Cliente"}</span></div>
      ${appt.clientPhone ? `<div class="row"><span>Telefone:</span><span>${appt.clientPhone}</span></div>` : ""}
      <div class="row"><span>Profissional:</span><span>${appt.professionalName ?? "-"}</span></div>
      <div class="line"></div>
      <p class="bold">Serviços e produtos:</p>
      ${itemsHtml}
      <div class="row"><span>Subtotal:</span><span>R$ ${subtotal.toFixed(2)}</span></div>
      ${discount > 0 ? `<div class="row discount"><span>Desconto:</span><span>- R$ ${discount.toFixed(2)}</span></div>` : ""}
      <div class="line"></div>
      <div class="row big"><span>TOTAL PAGO:</span><span>R$ ${totalPaid.toFixed(2)}</span></div>
      <div class="line"></div>
      <p class="bold">Formas de Pagamento:</p>
      ${splitsHtml}
      ${change > 0 ? `<div class="row"><span>Troco:</span><span>R$ ${change.toFixed(2)}</span></div>` : ""}
      <div class="line"></div>
      <div class="center footer">
        <p>Obrigado pela preferência!</p>
        <p>Impresso em ${format(now, "dd/MM/yyyy HH:mm")}</p>
      </div>
    </body>
    </html>
  `;
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => { win.print(); win.close(); }, 500);
  return true;
}

function printStandaloneSaleReceipt(receipt: StandaloneSaleReceiptSnapshot): boolean {
  const win = window.open("", "_blank", "width=400,height=600");
  if (!win) return false;
  const escapeHtml = (value: unknown) => String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
  const money = (value: number) => `R$ ${Number(value).toFixed(2).replace(".", ",")}`;
  const itemsHtml = receipt.products.map((product) =>
    `<div class="row"><span>${escapeHtml(`${product.quantity}x ${product.name}`)}</span><span>${money(product.quantity * product.unitPrice)}</span></div>`
  ).join("");
  const splitsHtml = receipt.splits.map((split) =>
    `<div class="row"><span>${escapeHtml(methodLabels[split.method])}</span><span>${money(Number(split.amount))}</span></div>`
  ).join("");
  win.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"/><title>Venda avulsa</title><style>@page{size:80mm auto;margin:2mm}*{box-sizing:border-box}html,body{width:76mm;margin:0 auto;padding:0}body{font-family:Arial,Helvetica,sans-serif;font-size:10.5pt;line-height:1.22;color:#000}.center{text-align:center}.line{border-top:1px dashed #000;margin:6px 0}.row{display:flex;justify-content:space-between;gap:8px;margin:3px 0}.row span:last-child{text-align:right}.big{font-size:13pt;font-weight:700}.footer{margin-top:8px;font-size:8.5pt;text-align:center}</style></head><body><div class="center"><h2>${escapeHtml(receipt.salonName)}</h2><p>VENDA AVULSA DE PRODUTOS</p></div><div class="line"></div><div class="row"><span>Data/Hora:</span><span>${escapeHtml(format(new Date(), "dd/MM/yyyy HH:mm"))}</span></div>${receipt.clientName ? `<div class="row"><span>Cliente:</span><span>${escapeHtml(receipt.clientName)}</span></div>` : ""}<div class="line"></div><p><strong>Produtos:</strong></p>${itemsHtml}${receipt.discount > 0 ? `<div class="row"><span>Desconto:</span><span>- ${money(receipt.discount)}</span></div>` : ""}<div class="line"></div><div class="row big"><span>TOTAL PAGO:</span><span>${money(receipt.totalPaid)}</span></div><div class="line"></div><p><strong>Formas de pagamento:</strong></p>${splitsHtml}${receipt.change > 0 ? `<div class="row"><span>Troco:</span><span>${money(receipt.change)}</span></div>` : ""}<div class="line"></div><div class="center footer"><p>Obrigado pela preferência!</p><p>Impresso em ${escapeHtml(format(new Date(), "dd/MM/yyyy HH:mm"))}</p></div></body></html>`);
  win.document.close();
  win.focus();
  setTimeout(() => { win.print(); win.close(); }, 500);
  return true;
}

// ─── Movimentações manuais de Caixa ─────────────────────────────────────────
type CashMovementDirection = "expense" | "income";

function ExpensesTab() {
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [direction, setDirection] = useState<CashMovementDirection>("expense");
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("dinheiro");
  const [notes, setNotes] = useState("");
  const utils = trpc.useUtils();
  const isIncome = direction === "income";
  const directionLabel = isIncome ? "Entrada" : "Saída";

  const { data: expenses, isLoading } = trpc.checkout.listExpenses.useQuery({});

  const addExpense = trpc.checkout.addExpense.useMutation({
    onSuccess: () => {
      const paymentLabel = paymentMethods.find((method) => method.value === paymentMethod)?.label ?? paymentMethod;
      showOperationSuccess({
        title: isIncome ? "Entrada registrada" : "Saída registrada",
        description: `${description.trim()} • ${amount || "R$ 0,00"} via ${paymentLabel}. O lançamento já aparece no movimento do caixa.`,
      });
      setDescription("");
      setAmount("");
      setNotes("");
      setPaymentMethod("dinheiro");
      utils.checkout.listExpenses.invalidate();
      utils.checkout.report.invalidate();
    },
    onError: (err) => toast.error(`Erro ao registrar ${directionLabel.toLowerCase()}: ${err.message}`),
  });

  const deleteExpense = trpc.checkout.deleteExpense.useMutation({
    onSuccess: () => {
      toast.success("Movimentação removida.");
      utils.checkout.listExpenses.invalidate();
      utils.checkout.report.invalidate();
    },
    onError: (err) => toast.error("Erro ao remover: " + err.message),
  });

  const handleSubmit = () => {
    const val = parseCurrencyInput(amount);
    if (!description.trim()) return toast.error(`Informe o motivo da ${directionLabel.toLowerCase()}.`);
    if (isNaN(val) || val <= 0) return toast.error("Informe um valor válido.");
    addExpense.mutate({ direction, description: description.trim(), amount: val, paymentMethod, notes: notes.trim() || undefined });
  };

  const movements = expenses ?? [];
  const totalEntries = movements
    .filter((movement: any) => movement.direction === "income")
    .reduce((sum: number, movement: any) => sum + Number(movement.amount ?? 0), 0);
  const totalExpenses = movements
    .filter((movement: any) => movement.direction !== "income")
    .reduce((sum: number, movement: any) => sum + Number(movement.amount ?? 0), 0);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="gap-3 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className={`flex items-center gap-2 ${isIncome ? "text-emerald-700" : "text-red-600"}`}>
            {isIncome ? <ArrowUpCircle className="w-5 h-5" /> : <ArrowDownCircle className="w-5 h-5" />}
            Registrar {directionLabel} de Caixa
          </CardTitle>
          <div className="grid grid-cols-2 gap-2" role="group" aria-label="Tipo de movimentação de caixa">
            <Button type="button" variant={isIncome ? "default" : "outline"} className={isIncome ? "min-h-11 bg-emerald-600 hover:bg-emerald-700" : "min-h-11 border-emerald-200 text-emerald-700 hover:bg-emerald-50"} onClick={() => setDirection("income")}><ArrowUpCircle className="mr-2 h-4 w-4" />Registrar Entrada</Button>
            <Button type="button" variant={!isIncome ? "default" : "outline"} className={!isIncome ? "min-h-11 bg-red-600 hover:bg-red-700" : "min-h-11 border-red-200 text-red-700 hover:bg-red-50"} onClick={() => setDirection("expense")}><ArrowDownCircle className="mr-2 h-4 w-4" />Registrar Saída</Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-1"><Label>Motivo / Descrição *</Label><Input placeholder={isIncome ? "Ex.: Troco inicial, reforço de caixa..." : "Ex.: Compra de material, conta de água..."} value={description} onChange={(event) => setDescription(event.target.value)} /></div>
            <div className="space-y-1"><Label>Valor (R$) *</Label><Input placeholder="R$ 0,00" value={amount} onChange={(event) => setAmount(formatCurrencyInput(event.target.value))} inputMode="decimal" /></div>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-1">
              <Label>Forma da movimentação</Label>
              <div className="flex flex-wrap gap-2">
                {paymentMethods.map((method) => <button key={method.value} type="button" onClick={() => setPaymentMethod(method.value)} className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-all ${paymentMethod === method.value ? method.color + " ring-2 ring-offset-1" : "border-border bg-background text-muted-foreground hover:bg-muted"}`}>{method.icon}{method.label}</button>)}
              </div>
              <p className={`mt-2 text-xs ${paymentMethod === "dinheiro" ? (isIncome ? "text-emerald-700" : "text-red-700") : "text-muted-foreground"}`}>
                {paymentMethod === "dinheiro"
                  ? isIncome ? "Entrada em dinheiro: será somada ao saldo físico deste turno e ficará identificada no fechamento. Não conta como venda ou comissão." : "Saída em dinheiro: será abatida do saldo físico deste turno e aparecerá descrita no fechamento."
                  : "Movimentações em outra forma não alteram o dinheiro físico do caixa e ficam registradas apenas para conferência."}
              </p>
            </div>
            <div className="space-y-1"><Label>Observações (opcional)</Label><Input placeholder="Detalhes adicionais..." value={notes} onChange={(event) => setNotes(event.target.value)} /></div>
          </div>
          <Button onClick={handleSubmit} disabled={addExpense.isPending} className={isIncome ? "min-h-11 bg-emerald-600 text-white hover:bg-emerald-700" : "min-h-11 bg-red-600 text-white hover:bg-red-700"}>
            {addExpense.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : isIncome ? <ArrowUpCircle className="mr-2 h-4 w-4" /> : <Plus className="mr-2 h-4 w-4" />}
            Registrar {directionLabel}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex flex-wrap items-center justify-between gap-3"><span className="flex items-center gap-2"><Receipt className="h-5 w-5" />Movimentações manuais</span>{movements.length > 0 && <span className="text-sm font-medium"><span className="text-emerald-700">Entradas: R$ {totalEntries.toFixed(2).replace(".", ",")}</span><span className="mx-2 text-muted-foreground">·</span><span className="text-red-600">Saídas: R$ {totalExpenses.toFixed(2).replace(".", ",")}</span></span>}</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div> : movements.length === 0 ? <div className="py-10 text-center text-muted-foreground"><AlertCircle className="mx-auto mb-2 h-10 w-10 opacity-30" /><p>Nenhuma entrada ou saída manual registrada ainda.</p></div> : <div className="space-y-2">
            {movements.map((movement: any) => {
              const movementIsIncome = movement.direction === "income";
              return <div key={movement.id} className={`flex items-center justify-between rounded-lg border p-3 ${movementIsIncome ? "border-emerald-100 bg-emerald-50" : "border-red-100 bg-red-50"}`}>
                <div className="min-w-0 flex-1"><p className="truncate text-sm font-medium">{movement.description}</p><div className="mt-0.5 flex flex-wrap items-center gap-2"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${movementIsIncome ? "bg-emerald-200 text-emerald-800" : "bg-red-200 text-red-800"}`}>{movementIsIncome ? "Entrada" : "Saída"}</span><span className={`rounded-full px-2 py-0.5 text-xs font-medium ${methodColors[movement.paymentMethod] ?? "bg-gray-100 text-gray-600"}`}>{methodLabels[movement.paymentMethod] ?? movement.paymentMethod}</span><span className="text-xs text-muted-foreground">{movement.registeredBy} · {format(new Date(movement.createdAt), "dd/MM HH:mm", { locale: ptBR })}</span>{movement.notes && <span className="truncate text-xs text-muted-foreground">· {movement.notes}</span>}</div></div>
                <div className="ml-4 flex items-center gap-3"><span className={`whitespace-nowrap font-bold ${movementIsIncome ? "text-emerald-700" : "text-red-600"}`}>{movementIsIncome ? "+" : "-"} R$ {Number(movement.amount ?? 0).toFixed(2).replace(".", ",")}</span><Button variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground hover:text-red-600" onClick={() => deleteExpense.mutate({ id: movement.id })} disabled={deleteExpense.isPending} aria-label={`Remover ${movementIsIncome ? "entrada" : "saída"}`}><Trash2 className="h-3.5 w-3.5" /></Button></div>
              </div>;
            })}
          </div>}
        </CardContent>
      </Card>
    </div>
  );
}

export default function AdminCheckout() {
  const [location, navigate] = useLocation();
  const { isMobile: isAppMobile } = useBreakpoint();
  const [selectedAppointment, setSelectedAppointment] = useState<number | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Detectar cargo do usuário logado para restringir acesso ao relatório
  const { data: staffCtx } = trpc.staff.myContext.useQuery();
  const isRecepcionista = staffCtx?.staffRole === "recepcionista";

  // Abre o caixa automaticamente se a URL tiver ?appointmentId=XXX
  const autoOpenApptId = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get("appointmentId");
    return id ? Number(id) : null;
  }, [location]);

  // Se veio da agenda (?appointmentId=XXX), retorna para a agenda após finalizar
  const cameFromAgenda = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return !!params.get("appointmentId");
  }, []); // apenas na montagem inicial

  // Múltiplas formas de pagamento
  const [splits, setSplits] = useState<PaymentSplitEntry[]>([{ method: "dinheiro", amount: "", installments: 1 }]);

  // Desconto
  const [discountAmount, setDiscountAmount] = useState<string>("0");
  const [discountReason, setDiscountReason] = useState<string>("");
  const [discountPinOpen, setDiscountPinOpen] = useState(false);
  const [discountApproved, setDiscountApproved] = useState(false);
  const [pendingDiscount, setPendingDiscount] = useState<{ amount: string; reason: string } | null>(null);

  // Valor cobrado customizado ("a partir de") — permite cobrar mais que o preço base sem PIN
  const [customPrice, setCustomPrice] = useState<string>("");

  // Fila de chamada
  const [addToQueueId, setAddToQueueId] = useState<number | null>(null);

  // Aba ativa do Caixa (controlado para suporte a select no mobile)
  const [activeTab, setActiveTab] = useState("pending");

  // Filtro de período e busca na aba Pendentes
  const [pendingPeriod, setPendingPeriod] = useState<"today" | "week" | "month">("today");
  const [pendingSearch, setPendingSearch] = useState("");

  // Report state — recepcionista fica travado em "today"
  const [periodPreset, setPeriodPreset] = useState<PeriodPreset>("today");
  // Forçar período "Hoje" quando recepcionista
  useEffect(() => {
    if (isRecepcionista) {
      setPeriodPreset("today");
    }
  }, [isRecepcionista]);
  const [customStart, setCustomStart] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [customEnd, setCustomEnd] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [reportView, setReportView] = useState<ReportView>("all");
  const [stationFilter, setStationFilter] = useState<string>("all");
  const [cashCount, setCashCount] = useState("");
  const [shiftPeriodType, setShiftPeriodType] = useState<"morning" | "afternoon" | "custom">("morning");
  const [shiftNotes, setShiftNotes] = useState("");
  const [shiftCustomStart, setShiftCustomStart] = useState(() => format(new Date(), "yyyy-MM-dd'T'00:00"));
  const [shiftCustomEnd, setShiftCustomEnd] = useState(() => format(new Date(), "yyyy-MM-dd'T'HH:mm"));
  const [shiftClock, setShiftClock] = useState(() => Date.now());
  const [selectedClosedShift, setSelectedClosedShift] = useState<any | null>(null);

  // O período não pode ficar congelado na hora em que a página foi aberta.
  // Ao abrir a aba ou mantê-la visível, a conferência usa o horário atual.
  useEffect(() => {
    if (activeTab !== "shift") return;
    setShiftClock(Date.now());
    const interval = window.setInterval(() => setShiftClock(Date.now()), 30_000);
    return () => window.clearInterval(interval);
  }, [activeTab]);

  // Troco automático (Dinheiro)
  const [cashReceived, setCashReceived] = useState<string>("");
  const [paymentReviewOpen, setPaymentReviewOpen] = useState(false);
  const [paymentReceipt, setPaymentReceipt] = useState<CheckoutReceiptSnapshot | null>(null);
  const [receiptPrintAttempted, setReceiptPrintAttempted] = useState(false);

  // Estorno com PIN
  const [refundPinOpen, setRefundPinOpen] = useState(false);
  const [refundTargetId, setRefundTargetId] = useState<number | null>(null);

  // Produtos adicionais no caixa
  const [checkoutProducts, setCheckoutProducts] = useState<Array<{ productId: number; name: string; quantity: number; unitPrice: number }>>([]); 
  const [productSearch, setProductSearch] = useState("");

  // Produtos utilizados no serviço (descontados da comissão)
  type UsedProductEntry = { productId: number; name: string; qty: number; usageType: "professional_own" | "internal_service"; unit: string; unitCost: number; notes: string; };
  const [usedProducts, setUsedProducts] = useState<UsedProductEntry[]>([]);
  const [usedProductSearch, setUsedProductSearch] = useState("");

  // Assistente no atendimento (abate % da comissão do profissional)
  const [assistantId, setAssistantId] = useState<number | null>(null);
  // Stable query inputs — objetos literais inline causam re-fetches infinitos
  const productsQueryInput = useMemo(() => ({ search: "", category: "" }), []);
  const clientsQueryInput = useMemo(() => ({ search: "", pageSize: 500 }), []);
  const { data: allProducts } = trpc.products.list.useQuery(productsQueryInput, { enabled: dialogOpen });

  // Venda avulsa de produtos
  const [saleProducts, setSaleProducts] = useState<Array<{ productId: number; name: string; quantity: number; unitPrice: number }>>([]);
  const [saleProductSearch, setSaleProductSearch] = useState("");
  const [saleClientName, setSaleClientName] = useState("");
  const [saleProfessional, setSaleProfessional] = useState<{ id: number; name: string } | null>(null);
  const [saleBranchId, setSaleBranchId] = useState("");
  const [saleRecipientSearch, setSaleRecipientSearch] = useState("");
  const [saleSplits, setSaleSplits] = useState<PaymentSplitEntry[]>([{ method: "dinheiro", amount: "" }]);
  const [saleDiscountAmount, setSaleDiscountAmount] = useState<string>("0");
  const [saleCashReceived, setSaleCashReceived] = useState<string>("");
  const [saleReviewOpen, setSaleReviewOpen] = useState(false);
  const [saleReceipt, setSaleReceipt] = useState<StandaloneSaleReceiptSnapshot | null>(null);
  const [saleReceiptPrintAttempted, setSaleReceiptPrintAttempted] = useState(false);
  const { data: allProductsForSale } = trpc.products.list.useQuery(productsQueryInput);
  const saleRecipientQueryInput = useMemo(() => ({ search: saleRecipientSearch.trim(), pageSize: 12 }), [saleRecipientSearch]);
  const { data: saleClientsData } = trpc.clients.list.useQuery(saleRecipientQueryInput, { enabled: saleRecipientSearch.trim().length >= 2 });
  const { data: allProfessionals = [] } = (trpc as any).professionals.listAdmin.useQuery();
  const { data: saleBranches = [] } = (trpc as any).branches.listForStaff.useQuery();
  const [showClientSuggestions, setShowClientSuggestions] = useState(false);
  useEffect(() => {
    if (saleBranches.length === 0) return;
    const saved = window.localStorage.getItem("beauty-current-sale-branch");
    const validSaved = saleBranches.some((branch: any) => String(branch.id) === saved);
    if (validSaved) setSaleBranchId(saved!);
    else if (saleBranches.length === 1) setSaleBranchId(String(saleBranches[0].id));
  }, [saleBranches]);
  useEffect(() => {
    if (saleBranchId) window.localStorage.setItem("beauty-current-sale-branch", saleBranchId);
  }, [saleBranchId]);
  const saleRecipientSuggestions = useMemo(() => {
    const query = saleRecipientSearch.trim().toLocaleLowerCase("pt-BR");
    if (query.length < 2) return [];
    const clients = (saleClientsData?.clients ?? []).map((client: any) => ({
      type: "client" as const,
      id: client.id,
      name: client.clientName ?? "Cliente sem nome",
      detail: client.clientPhone ?? "Cliente cadastrado",
    }));
    const professionals = (allProfessionals as any[])
      .filter((professional) => professional.isActive && professional.name.toLocaleLowerCase("pt-BR").includes(query))
      .slice(0, 8)
      .map((professional) => ({
        type: "professional" as const,
        id: professional.id,
        name: professional.name,
        detail: professional.executesServices ? "Profissional cadastrado" : "Colaborador administrativo",
      }));
    return [...professionals, ...clients].slice(0, 14);
  }, [saleRecipientSearch, saleClientsData, allProfessionals]);
  const handleSaleRecipientChange = (value: string) => {
    setSaleRecipientSearch(value);
    setSaleClientName(value);
    setSaleProfessional(null);
    setShowClientSuggestions(value.trim().length >= 2);
  };
  const filteredSaleProducts = (allProductsForSale ?? []).filter(p =>
    Number(p.stockQty) > 0 && (saleProductSearch === "" || p.name.toLowerCase().includes(saleProductSearch.toLowerCase()))
  );
  const saleTotalProducts = saleProducts.reduce((sum, p) => sum + p.quantity * p.unitPrice, 0);
  const saleDiscountVal = Number(saleDiscountAmount) || 0;
  const saleFinalTotal = Math.max(0, saleTotalProducts - saleDiscountVal);
  const saleProfessionalConsumptionTotal = saleProducts.reduce((sum, item) => {
    const product = (allProductsForSale ?? []).find((candidate: any) => candidate.id === item.productId);
    const configuredCost = Number((product as any)?.professionalUseCost ?? 0);
    const fallbackCost = Number((product as any)?.costPrice ?? 0);
    return sum + item.quantity * (configuredCost > 0 ? configuredCost : fallbackCost);
  }, 0);
  const isProfessionalConsumption = !!saleProfessional;
  const saleProfessionalProductsWithoutCost = saleProducts.filter((item) => {
    const product = (allProductsForSale ?? []).find((candidate: any) => candidate.id === item.productId);
    return Number((product as any)?.professionalUseCost ?? 0) <= 0 && Number((product as any)?.costPrice ?? 0) <= 0;
  });
  const saleTotalSplits = saleSplits.reduce((sum, s) => sum + (Number(s.amount) || 0), 0);
  const saleRemaining = Math.max(0, saleFinalTotal - saleTotalSplits);
  const saleIsBalanced = Math.abs(saleTotalSplits - saleFinalTotal) < 0.01;
  const saleHasSingleCashSplit = saleSplits.length === 1 && saleSplits[0].method === "dinheiro";
  const saleCashReceivedVal = Number(saleCashReceived) || 0;
  const saleTroco = saleHasSingleCashSplit && saleCashReceivedVal > 0 ? Math.max(0, saleCashReceivedVal - saleFinalTotal) : 0;
  const filteredProducts = (allProducts ?? []).filter(p =>
    Number(p.stockQty) > 0 && (productSearch === "" || p.name.toLowerCase().includes(productSearch.toLowerCase()))
  );
  const productsTotal = checkoutProducts.reduce((sum, p) => sum + p.quantity * p.unitPrice, 0);

  // Produtos de uso interno (para desconto de comissão)
  const filteredUsedProducts = (allProducts ?? []).filter(p =>
    !!p.deductFromCommission &&
    Number(p.stockQty) > 0 &&
    (usedProductSearch === "" || p.name.toLowerCase().includes(usedProductSearch.toLowerCase()))
  );
  const usedProductsDeductTotal = usedProducts.reduce((sum, up) => sum + up.qty * up.unitCost, 0);

  const utils = trpc.useUtils();

  const refundWithAuth = trpc.authorization.refundPayment.useMutation({
    onSuccess: () => {
      toast.success("Pagamento estornado com autorização.");
      (utils as any).checkout.report.invalidate();
      (utils as any).checkout.listUnpaid.invalidate();
      setRefundPinOpen(false);
      setRefundTargetId(null);
    },
    onError: (e: { message: string }) => toast.error(e.message),
  });

  const reportDates = useMemo(() => {
    const now = new Date();
    if (periodPreset === "today") return { start: startOfDay(now), end: endOfDay(now) };
    if (periodPreset === "week") return { start: startOfWeek(now, { locale: ptBR }), end: endOfWeek(now, { locale: ptBR }) };
    if (periodPreset === "month") return { start: startOfMonth(now), end: endOfMonth(now) };
    return {
      start: startOfDay(new Date(customStart + "T00:00:00")),
      end: endOfDay(new Date(customEnd + "T23:59:59")),
    };
  }, [periodPreset, customStart, customEnd]);

  const { data: salonData } = trpc.salon.getMySalon.useQuery();
  const panelProfile = getQueuePanelProfile((salonData as any)?.queuePanelProfile);
  const salonName = (salonData as any)?.name ?? "Salão";

  const pendingQueryInput = useMemo(() => ({ period: pendingPeriod }), [pendingPeriod]);
  const { data: unpaidRaw, isLoading } = trpc.checkout.listUnpaid.useQuery(pendingQueryInput);
  // Filtrar localmente por busca (nome do cliente ou serviço)
  const unpaid = useMemo(() => {
    if (!unpaidRaw) return unpaidRaw;
    if (!pendingSearch.trim()) return unpaidRaw;
    const q = pendingSearch.toLowerCase();
    return unpaidRaw.filter((a: any) =>
      (a.clientName ?? "").toLowerCase().includes(q) ||
      (a.serviceName ?? "").toLowerCase().includes(q) ||
      (a.clientPhone ?? "").includes(q)
    );
  }, [unpaidRaw, pendingSearch]);
  const nextPendingPayment = useMemo(
    () => getNextMobilePendingPayment(unpaid ?? []),
    [unpaid],
  );
  // Stable query inputs — inline objects cause infinite re-fetches with Date values
  const reportQueryInput = useMemo(
    () => ({ startDate: reportDates.start, endDate: reportDates.end }),
    [reportDates]
  );
  const { data: report, isLoading: reportLoading } = trpc.checkout.report.useQuery(
    reportQueryInput,
    { enabled: true }
  );
  const canOperateCashShift = !!staffCtx && staffCtx.staffRole !== "funcionario";
  const isCashManager = staffCtx?.staffRole === "admin";
  const { data: currentCashShift, isLoading: currentCashShiftLoading, isError: currentCashShiftError, refetch: refetchCurrentCashShift } = (trpc as any).checkout.currentShift.useQuery();
  const { data: cashShiftHistory = [] } = (trpc as any).checkout.shiftHistory.useQuery({ limit: 50 });
  const shiftClosingBounds = useMemo(() => {
    const now = new Date(shiftClock);
    const dayStart = startOfDay(now);
    const afternoonStart = new Date(now); afternoonStart.setHours(13, 0, 0, 0);
    const openedAt = currentCashShift?.periodStart ? new Date(currentCashShift.periodStart) : null;
    const notBeforeOpening = (candidate: Date) => openedAt && candidate.getTime() < openedAt.getTime() ? openedAt : candidate;
    if (shiftPeriodType === "morning") return { start: notBeforeOpening(dayStart), end: now.getTime() < afternoonStart.getTime() ? now : afternoonStart };
    if (shiftPeriodType === "afternoon") return { start: notBeforeOpening(afternoonStart), end: now };
    // O modo personalizado é a exceção intencional: usa exatamente o intervalo
    // informado, inclusive quando ele começa antes da abertura do turno atual.
    return { start: new Date(shiftCustomStart), end: new Date(shiftCustomEnd) };
  }, [shiftPeriodType, shiftCustomStart, shiftCustomEnd, currentCashShift?.periodStart, shiftClock]);
  const isAfternoonShiftAvailable = useMemo(() => {
    const now = new Date(shiftClock);
    const afternoonStart = new Date(now); afternoonStart.setHours(13, 0, 0, 0);
    return now.getTime() > afternoonStart.getTime();
  }, [shiftClock]);
  const hasValidShiftClosingBounds = !Number.isNaN(shiftClosingBounds.start.getTime())
    && !Number.isNaN(shiftClosingBounds.end.getTime())
    && shiftClosingBounds.end.getTime() > shiftClosingBounds.start.getTime()
    && (shiftPeriodType !== "custom" || shiftClosingBounds.end.getTime() <= Date.now());
  const shiftReportInput = useMemo(() => ({ startDate: shiftClosingBounds.start, endDate: shiftClosingBounds.end }), [shiftClosingBounds]);
  const { data: shiftReport } = trpc.checkout.report.useQuery(shiftReportInput, { enabled: canOperateCashShift && !!currentCashShift });
  const { data: shiftExpenses = [] } = trpc.checkout.listExpenses.useQuery(shiftReportInput, { enabled: canOperateCashShift && !!currentCashShift });
  const shiftCashSalesTotal = calculateCashReceivedForPhysicalCount(shiftReport?.payments ?? []);
  const shiftCashManualEntriesTotal = (shiftExpenses ?? []).filter((movement: any) => movement.direction === "income" && movement.paymentMethod === "dinheiro").reduce((sum: number, movement: any) => sum + Number(movement.amount ?? 0), 0);
  const shiftCashExpensesTotal = (shiftExpenses ?? []).filter((movement: any) => movement.direction !== "income" && movement.paymentMethod === "dinheiro").reduce((sum: number, movement: any) => sum + Number(movement.amount ?? 0), 0);
  const shiftExpectedCashTotal = shiftCashSalesTotal + shiftCashManualEntriesTotal - shiftCashExpensesTotal;
  const shiftLateCashEntries = useMemo(
    () => (shiftReport?.payments ?? []).filter((payment: any) => payment.isLateCashEntry),
    [shiftReport?.payments],
  );
  const shiftLateCashEntriesTotal = shiftLateCashEntries.reduce(
    (total: number, payment: any) => total + Number(payment.totalPrice ?? 0),
    0,
  );
  const shiftLateCashEntriesCashTotal = shiftLateCashEntries.reduce(
    (total: number, payment: any) => total + getCashAmountFromPayment(payment),
    0,
  );
  const selectedShiftPaymentDetails = useMemo(() => {
    try {
      const parsed = JSON.parse(selectedClosedShift?.paymentDetails ?? "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, [selectedClosedShift?.paymentDetails]);
  const selectedShiftExpenseDetails = useMemo(() => {
    try {
      const parsed = JSON.parse(selectedClosedShift?.expenseDetails ?? "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, [selectedClosedShift?.expenseDetails]);
  const selectedShiftTotalsByMethod = useMemo(() => {
    try {
      const parsed = JSON.parse(selectedClosedShift?.totalByMethod ?? "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, [selectedClosedShift?.totalByMethod]);
  const selectedShiftLateCashEntries = useMemo(
    () => selectedShiftPaymentDetails.filter((payment: any) => payment.isLateCashEntry),
    [selectedShiftPaymentDetails],
  );
  const selectedShiftLateCashEntriesTotal = selectedShiftLateCashEntries.reduce(
    (total: number, payment: any) => total + Number(payment.amount ?? 0),
    0,
  );
  const selectedShiftLateCashEntriesCashTotal = selectedShiftLateCashEntries.reduce(
    (total: number, payment: any) => total + getCashAmountFromPayment(payment),
    0,
  );
  const { data: standaloneSales, isLoading: standaloneSalesLoading } = trpc.products.listStandaloneSales.useQuery(
    reportQueryInput,
    { enabled: true }
  );
  const { data: reportExpenses } = trpc.checkout.listExpenses.useQuery(
    reportQueryInput,
    { enabled: true }
  );
  const totalReportManualEntries = (reportExpenses ?? []).filter((movement: any) => movement.direction === "income").reduce((sum: number, movement: any) => sum + Number(movement.amount ?? 0), 0);
  const totalReportExpenses = (reportExpenses ?? []).filter((movement: any) => movement.direction !== "income").reduce((sum: number, movement: any) => sum + Number(movement.amount ?? 0), 0);
  const cashSalesTotal = calculateCashReceivedForPhysicalCount(report?.payments ?? []);
  const cashManualEntriesTotal = (reportExpenses ?? []).filter((movement: any) => movement.direction === "income" && movement.paymentMethod === "dinheiro").reduce((sum: number, movement: any) => sum + Number(movement.amount ?? 0), 0);
  const cashExpensesTotal = (reportExpenses ?? []).filter((movement: any) => movement.direction !== "income" && movement.paymentMethod === "dinheiro").reduce((sum: number, movement: any) => sum + Number(movement.amount ?? 0), 0);
  const expectedCashTotal = cashSalesTotal + cashManualEntriesTotal - cashExpensesTotal;
  const reportLateCashEntries = useMemo(
    () => (report?.payments ?? []).filter((payment: any) => payment.isLateCashEntry),
    [report?.payments],
  );
  const reportLateCashEntriesTotal = reportLateCashEntries.reduce(
    (total: number, payment: any) => total + Number(payment.totalPrice ?? 0),
    0,
  );
  const reportLateCashEntriesCashTotal = reportLateCashEntries.reduce(
    (total: number, payment: any) => total + getCashAmountFromPayment(payment),
    0,
  );
  const countedCashTotal = cashCount.trim() ? parseCurrencyInput(cashCount) : null;
  const cashVariance = countedCashTotal === null ? null : countedCashTotal - expectedCashTotal;
  const { data: queue, isLoading: queueLoading } = trpc.queue.list.useQuery(undefined, {
    refetchInterval: 5000,
  });
  const closeCashShiftMutation = (trpc as any).checkout.closeShift.useMutation({
    onSuccess: () => {
      toast.success("Caixa encerrado. Consulte o detalhamento abaixo quando precisar.");
      setShiftNotes("");
      (utils as any).checkout.currentShift.invalidate();
      (utils as any).checkout.shiftHistory.invalidate();
      (utils as any).checkout.report.invalidate();
      (utils as any).checkout.listExpenses.invalidate();
    },
    onError: (error: { message: string }) => toast.error(error.message),
  });
  const reopenCashShiftMutation = (trpc as any).checkout.reopenShift.useMutation({
    onSuccess: () => {
      toast.success("Caixa reaberto pela gerência. A ação foi registrada na auditoria.");
      (utils as any).checkout.currentShift.invalidate();
      (utils as any).checkout.shiftHistory.invalidate();
      (utils as any).checkout.report.invalidate();
    },
    onError: (error: { message: string }) => toast.error(error.message),
  });
  const assemblyAssignmentDate = useMemo(() => format(new Date(), "yyyy-MM-dd"), []);
  const { data: assemblyStaff = [] } = (trpc as any).queue.assemblyStaff.useQuery();
  const { data: assemblyWeeklySchedules = [] } = (trpc as any).queue.getAssemblyWeeklySchedules.useQuery();
  const { data: assemblyResponsible } = (trpc as any).queue.getAssemblyResponsible.useQuery({ assignmentDate: assemblyAssignmentDate });
  const weeklyAssemblyByDay = useMemo(
    () => new Map((assemblyWeeklySchedules as any[]).map((schedule) => [schedule.dayOfWeek, schedule.staffId])),
    [assemblyWeeklySchedules],
  );
  const [assemblyAlertClock, setAssemblyAlertClock] = useState(() => Date.now());
  const alertedAssemblyIds = useRef<Set<number>>(new Set());
  const overdueAssemblyItems = useMemo(() => (queue ?? []).filter((item: any) => {
    if (item.status !== "preparing" || !item.preparationStartedAt) return false;
    return assemblyAlertClock - new Date(item.preparationStartedAt).getTime() >= panelProfile.preparationAlertMinutes * 60 * 1000;
  }), [queue, assemblyAlertClock, panelProfile.preparationAlertMinutes]);

  useEffect(() => {
    const interval = window.setInterval(() => setAssemblyAlertClock(Date.now()), 15_000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    const newlyOverdue = overdueAssemblyItems.filter((item: any) => !alertedAssemblyIds.current.has(item.id));
    if (newlyOverdue.length === 0) return;
    newlyOverdue.forEach((item: any) => alertedAssemblyIds.current.add(item.id));
    try {
      const audio = new AudioContext();
      [740, 880, 740].forEach((frequency, index) => {
        const oscillator = audio.createOscillator();
        const gain = audio.createGain();
        oscillator.connect(gain);
        gain.connect(audio.destination);
        oscillator.frequency.value = frequency;
        gain.gain.setValueAtTime(0.22, audio.currentTime + index * 0.18);
        gain.gain.exponentialRampToValueAtTime(0.001, audio.currentTime + index * 0.18 + 0.16);
        oscillator.start(audio.currentTime + index * 0.18);
        oscillator.stop(audio.currentTime + index * 0.18 + 0.18);
      });
    } catch {
      // Alguns navegadores bloqueiam som até a primeira interação da equipe.
    }
  }, [overdueAssemblyItems]);

  const registerPaymentSplit = trpc.checkout.registerPaymentSplit.useMutation({
    onSuccess: (data: any) => {
      toast.success("Pagamento registrado com sucesso!");
      if (data.clientPhone) toast.success("Mensagem WhatsApp enviada!", { icon: "💬" });
      (utils as any).checkout.listUnpaid.invalidate();
      (utils as any).checkout.report.invalidate();
      utils.queue.list.invalidate();
      // A impressão só é aberta em uma ação explícita depois do registro,
      // para a equipe poder confirmar que o comprovante foi exibido.
      if (selectedAppt) {
        setPaymentReceipt({
          appointment: selectedAppt,
          splits: splits.map((split) => ({ ...split })),
          discount: Number(discountAmount) || 0,
          subtotal: totalPrice,
          totalPaid: finalTotal,
          change: troco,
          salonName,
          items: [
            {
              name: commandaServices.length > 1
                ? `Serviços (${commandaServices.map((service: any) => service.serviceName).join(", ")})`
                : (commandaServices[0]?.serviceName ?? (selectedAppt as any).serviceName ?? "Serviço"),
              amount: effectiveServicePrice,
            },
            ...checkoutProducts.map((product) => ({
              name: `${product.quantity}x ${product.name}`,
              amount: product.quantity * product.unitPrice,
            })),
          ],
        });
        setReceiptPrintAttempted(false);
      }
      setPaymentReviewOpen(false);
      setDialogOpen(false);
      setSelectedAppointment(null);
      setSplits([{ method: "dinheiro", amount: "", installments: 1 }]);
      setDiscountAmount("0");
      setDiscountReason("");
      setDiscountApproved(false);
      setUsedProducts([]);
      setUsedProductSearch("");
      setAssistantId(null);
    },
    onError: (e: any) => toast.error(e.message),
  });

  const sellProductsMutation = trpc.checkout.sellProducts.useMutation({
    onSuccess: (data: any) => {
      const wasProfessionalConsumption = !!data.professionalId;
      toast.success(wasProfessionalConsumption
        ? `Consumo registrado. R$ ${Number(data.totalDeducted ?? 0).toFixed(2)} será abatido da comissão.`
        : "Venda avulsa registrada com sucesso!");
      utils.products.list.invalidate();
      (utils as any).products.listStandaloneSales.invalidate();
      (utils as any).checkout.report.invalidate();
      (utils as any).commissions.report.invalidate();
      if (!wasProfessionalConsumption) {
        setSaleReceipt({
          salonName,
          clientName: saleClientName,
          products: saleProducts.map((product) => ({ ...product })),
          splits: saleSplits.map((split) => ({ ...split })),
          discount: saleDiscountVal,
          totalPaid: saleFinalTotal,
          change: saleTroco,
        });
        setSaleReceiptPrintAttempted(false);
      }
      setSaleReviewOpen(false);
      // Resetar estado da venda avulsa
      setSaleProducts([]);
      setSaleProductSearch("");
      setSaleClientName("");
      setSaleProfessional(null);
      setSaleRecipientSearch("");
      setSaleSplits([{ method: "dinheiro", amount: "" }]);
      setSaleDiscountAmount("0");
      setSaleCashReceived("");
    },
    onError: (e: any) => toast.error(e.message),
  });

  const saleActionDisabled = isMobileSaleActionDisabled({ productCount: saleProducts.length, requiresBranch: saleBranches.length > 0, hasBranch: !!saleBranchId, isProfessionalConsumption, hasProductsWithoutCost: saleProfessionalProductsWithoutCost.length > 0, isBalanced: saleIsBalanced, isSubmitting: sellProductsMutation.isPending });
  const openSaleReview = () => {
    if (!isProfessionalConsumption && !saleIsBalanced) {
      toast.error(`Valor não confere. Faltam R$ ${saleRemaining.toFixed(2)} para cobrir o total.`);
      return;
    }
    if (saleProducts.length === 0 || saleActionDisabled) return;
    setSaleReviewOpen(true);
  };

  const submitSale = () => {
    if (saleProducts.length === 0 || saleActionDisabled) return;
    sellProductsMutation.mutate({
      clientName: isProfessionalConsumption ? "" : saleClientName,
      professionalId: saleProfessional?.id,
      branchId: saleBranchId ? Number(saleBranchId) : undefined,
      products: saleProducts.map(p => ({ productId: p.productId, quantity: p.quantity, unitPrice: p.unitPrice })),
      splits: isProfessionalConsumption ? [] : saleSplits.map(s => ({ paymentMethod: s.method, amount: Number(s.amount) })),
      discountAmount: isProfessionalConsumption ? 0 : saleDiscountVal,
    });
  };

  const applyDiscountWithPin = trpc.checkout.applyDiscountWithPin.useMutation({
    onSuccess: () => {
      toast.success("Desconto autorizado!");
      setDiscountApproved(true);
      setDiscountPinOpen(false);
    },
    onError: (e: any) => toast.error(e.message),
  });

  const addToQueueMutation = trpc.queue.add.useMutation({
    onSuccess: () => {
      toast.success("Cliente adicionado à fila!");
      utils.queue.list.invalidate();
      setAddToQueueId(null);
    },
    onError: (e: any) => toast.error(e.message),
  });

  const updateQueueStatus = trpc.queue.updateStatus.useMutation({
    onSuccess: () => utils.queue.list.invalidate(),
    onError: (e: any) => toast.error(e.message),
  });

  const moveQueueItem = (trpc as any).queue.move.useMutation({
    onSuccess: (result: any) => {
      utils.queue.list.invalidate();
      if (!result?.unchanged) toast.success("Ordem da fila atualizada.");
    },
    onError: (e: any) => toast.error(e.message),
  });

  const startQueuePreparation = (trpc as any).queue.startPreparation.useMutation({
    onSuccess: () => {
      utils.queue.list.invalidate();
      toast.success("Montagem iniciada e responsável definido.");
    },
    onError: (error: any) => toast.error(error.message),
  });

  const setQueueAssemblyResponsible = (trpc as any).queue.setAssemblyResponsible.useMutation({
    onSuccess: () => {
      utils.queue.list.invalidate();
      toast.success("Responsável deste atendimento atualizado.");
    },
    onError: (error: any) => toast.error(error.message),
  });

  const setAssemblyShiftResponsible = (trpc as any).queue.setAssemblyShiftResponsible.useMutation({
    onSuccess: () => {
      (utils as any).queue.getAssemblyResponsible.invalidate();
      toast.success("Responsável da montagem atualizado para o turno de hoje.");
    },
    onError: (error: any) => toast.error(error.message),
  });

  const setAssemblyWeeklySchedule = (trpc as any).queue.setAssemblyWeeklySchedule.useMutation({
    onSuccess: () => {
      (utils as any).queue.getAssemblyWeeklySchedules.invalidate();
      toast.success("Escala semanal de montagem atualizada.");
    },
    onError: (error: any) => toast.error(error.message),
  });

  const removeFromQueue = trpc.queue.remove.useMutation({
    onSuccess: () => utils.queue.list.invalidate(),
    onError: (e: any) => toast.error(e.message),
  });

  const clearDone = trpc.queue.clearDone.useMutation({
    onSuccess: () => { utils.queue.list.invalidate(); toast.success("Concluídos removidos da fila."); },
  });

  // Alterar serviço na fila
  const [changeServiceItem, setChangeServiceItem] = useState<{ id: number; serviceName: string } | null>(null);
  const [newServiceName, setNewServiceName] = useState("");
  const updateQueueServiceMutation = (trpc as any).queue.updateService.useMutation({
    onSuccess: () => {
      utils.queue.list.invalidate();
      toast.success("Serviço atualizado na fila!");
      setChangeServiceItem(null);
      setNewServiceName("");
    },
    onError: (e: any) => toast.error(e.message),
  });
  const { data: adminServices } = trpc.services.listAdmin.useQuery();
  const { data: selectedApptServices = [] } = (trpc as any).appointments.getServices.useQuery(
    { appointmentId: selectedAppointment! },
    { enabled: !!selectedAppointment }
  );

  // Monta lista de serviços para a comanda com nome do serviço e profissional executor
  const commandaServices = useMemo(() => {
    if (!selectedApptServices || selectedApptServices.length === 0) return [];
    return selectedApptServices.map((s: any) => {
      const svc = adminServices?.find((sv: any) => sv.id === s.serviceId);
      const prof = s.professionalId
        ? (allProfessionals as any[]).find((p: any) => p.id === s.professionalId)
        : null;
      return {
        serviceName: svc?.name ?? s.serviceName ?? "Serviço",
        price: s.price ?? svc?.price ?? "0",
        professionalName: prof?.name ?? undefined,
      };
    });
  }, [selectedApptServices, adminServices, allProfessionals]);

  const handleOpenDialog = (appointmentId: number) => {
    const appt = unpaid?.find(a => a.id === appointmentId);
    setSelectedAppointment(appointmentId);
    setCheckoutProducts([]);
    setProductSearch("");
    setSplits([{ method: "dinheiro", amount: Number(appt?.totalPrice ?? 0).toFixed(2), installments: 1 }]);
    setDiscountAmount("0");
    setDiscountReason("");
    setDiscountApproved(false);
    setCashReceived("");
    setAssistantId(null);
    setCustomPrice(""); // resetar valor customizado
    setDialogOpen(true);
    // Adicionar automaticamente à fila de chamada ao abrir o caixa (se ainda não estiver na fila)
    if (appt) {
      const alreadyInQueue = queue?.some((q: any) => q.appointmentId === appointmentId && q.status !== "done");
      if (!alreadyInQueue) {
        addToQueueMutation.mutate({
          appointmentId: appt.id,
          clientName: appt.clientName ?? (appt as any).userName ?? "Cliente",
          serviceName: (appt as any).serviceName ?? "Serviço",
          professionalName: (appt as any).professionalName ?? "-",
          professionalId: appt.professionalId ?? undefined,
        });
      }
    }
  };

  const selectedAppt = unpaid?.find((a: any) => a.id === selectedAppointment);

  // Auto-abre o caixa quando appointmentId vem na URL (ex: /checkout?appointmentId=123)
  useEffect(() => {
    if (autoOpenApptId && unpaid && unpaid.length > 0) {
      const found = unpaid.find((a: any) => a.id === autoOpenApptId);
      if (found && selectedAppointment !== autoOpenApptId) {
        handleOpenDialog(autoOpenApptId);
        // Remove o param da URL sem recarregar a página
        const url = new URL(window.location.href);
        url.searchParams.delete("appointmentId");
        window.history.replaceState({}, "", url.toString());
      }
    }
  }, [autoOpenApptId, unpaid]);

  const totalSplits = splits.reduce((sum, s) => sum + (Number(s.amount) || 0), 0);
  // Preço base do serviço (mínimo)
  const baseServicePrice = Number(selectedAppt?.totalPrice ?? 0);
  // Valor customizado: se informado e maior que o base, usa o customizado; senão usa o base
  const customPriceVal = customPrice !== "" ? Number(customPrice) : baseServicePrice;
  const effectiveServicePrice = customPriceVal > baseServicePrice ? customPriceVal : baseServicePrice;
  const totalPrice = effectiveServicePrice + productsTotal;
  const discountVal = Number(discountAmount) || 0;
  const finalTotal = Math.max(0, totalPrice - discountVal);
  const remaining = Math.max(0, finalTotal - totalSplits);
  const isBalanced = Math.abs(totalSplits - finalTotal) < 0.01;

  // Troco: só calcula quando há exatamente 1 split em Dinheiro
  const hasSingleCashSplit = splits.length === 1 && splits[0].method === "dinheiro";
  const cashReceivedVal = Number(cashReceived) || 0;
  const troco = hasSingleCashSplit && cashReceivedVal > 0 ? Math.max(0, cashReceivedVal - finalTotal) : 0;

  const handleAddSplit = () => {
    setSplits(prev => [...prev, { method: "pix", amount: remaining > 0 ? remaining.toFixed(2) : "", installments: 1 }]);
  };

  const handleRemoveSplit = (idx: number) => {
    setSplits(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSplitChange = (idx: number, field: "method" | "amount" | "installments", value: string) => {
    setSplits((prev) => prev.map((split, index) => {
      if (index !== idx) return split;
      if (field === "method") {
        const method = value as PaymentMethod;
        return { ...split, method, installments: method === "credito" ? split.installments || 1 : 1 };
      }
      if (field === "installments") return { ...split, installments: Math.min(12, Math.max(1, Number(value) || 1)) };
      return { ...split, amount: value };
    }));
  };

  const openPaymentReview = () => {
    if (!selectedAppointment || splits.length === 0) return;
    if (!isBalanced) {
      toast.error(`Valor não confere. Faltam R$ ${remaining.toFixed(2)} para cobrir o total.`);
      return;
    }
    setPaymentReviewOpen(true);
  };

  const submitConfirmedPayment = () => {
    if (!selectedAppointment || splits.length === 0 || !isBalanced) return;
    const primaryMethod = splits[0].method;
    registerPaymentSplit.mutate({
      appointmentId: selectedAppointment,
      splits: splits.map(s => ({ paymentMethod: s.method, amount: Number(s.amount), installments: s.installments })),
      discountAmount: discountVal,
      discountReason,
      primaryMethod,
      products: checkoutProducts.map(p => ({ productId: p.productId, quantity: p.quantity, unitPrice: p.unitPrice })),
      usedProducts: usedProducts.map(up => ({ productId: up.productId, qty: up.qty, usageType: up.usageType, notes: up.notes || undefined })),
      assistantId: assistantId ?? undefined,
    });
  };

  const handleAddToQueue = (appt: any) => {
    addToQueueMutation.mutate({
      appointmentId: appt.id,
      clientName: appt.clientName ?? appt.userName ?? "Cliente",
      serviceName: appt.serviceName ?? "Serviço",
      professionalName: appt.professionalName ?? "-",
      professionalId: appt.professionalId ?? undefined, // para buscar stationName automaticamente
    });
  };

const queueStatusLabel: Record<string, string> = {
  waiting: "Aguardando",
  preparing: "Montagem do biquíni",
  called: "Chamado",
    in_service: "Em atendimento",
    done: "Concluído",
  };
const queueStatusColor: Record<string, string> = {
  waiting: "bg-amber-100 text-amber-700",
  preparing: "bg-violet-100 text-violet-700",
  called: "bg-blue-100 text-blue-700",
    in_service: "bg-green-100 text-green-700",
    done: "bg-gray-100 text-gray-500",
  };

  return (
    <div className={isAppMobile ? "p-3 space-y-4" : "p-3 sm:p-4 md:p-6 space-y-4 md:space-y-6"}>
      {/* Header */}
      <div className={isAppMobile ? "flex flex-col items-stretch gap-3" : "flex flex-col items-start sm:flex-row sm:items-center justify-between gap-3"}>
        <div>
          <h1 className="text-2xl font-[Cormorant_Garamond,serif] font-semibold text-foreground">Caixa</h1>
          <p className="text-sm text-muted-foreground mt-1">Registre pagamentos e acompanhe o faturamento</p>
        </div>
        <div className={isAppMobile ? "flex items-center justify-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3" : "flex items-center justify-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 w-full sm:w-auto"}>
          <DollarSign className="w-4 h-4 text-amber-600" />
          <span className="text-sm font-medium text-amber-700">
            {unpaid?.length ?? 0} aguardando pagamento
          </span>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        {/* Em celular, inclusive Androids que reportam largura de tablet, o seletor evita abas comprimidas. */}
        {isAppMobile ? <div className="mb-2">
          <Select value={activeTab} onValueChange={setActiveTab}>
            <SelectTrigger className="h-12 w-full rounded-xl text-base">
              <SelectValue placeholder="Selecione a seção" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pendentes</SelectItem>
              <SelectItem value="queue">Fila de Chamada</SelectItem>
              <SelectItem value="sell">Venda Avulsa</SelectItem>
              <SelectItem value="expenses">Saídas</SelectItem>
              <SelectItem value="report">Relatório</SelectItem>
              {canOperateCashShift && <SelectItem value="shift">Fechamento</SelectItem>}
            </SelectContent>
          </Select>
        </div> : <TabsList>
          <TabsTrigger value="pending">Pendentes</TabsTrigger>
          <TabsTrigger value="queue">
            <Users className="w-4 h-4 mr-1" />
            Fila de Chamada
          </TabsTrigger>
          <TabsTrigger value="sell">
            <Package className="w-4 h-4 mr-1" />
            Venda Avulsa
          </TabsTrigger>
          <TabsTrigger value="expenses">
            <ArrowDownCircle className="w-4 h-4 mr-1" />
            Saídas
          </TabsTrigger>
          <TabsTrigger value="report">
            <TrendingUp className="w-4 h-4 mr-1" />
            Relatório
          </TabsTrigger>
          {canOperateCashShift && <TabsTrigger value="shift">Fechamento</TabsTrigger>}
        </TabsList>}

        {/* ─── Aba Pendentes ─────────────────────────────────────────────────── */}
        <TabsContent value="pending" className="mt-4 space-y-4">
          {/* Filtro de período + busca */}
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex gap-1 bg-muted/50 rounded-lg p-1">
              {(["today", "week", "month"] as const).map((p) => (
                <button
                  key={p}
                  onClick={() => setPendingPeriod(p)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                    pendingPeriod === p
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground hover:bg-background/60"
                  }`}
                >
                  {p === "today" ? "Hoje" : p === "week" ? "Semana" : "Mês"}
                </button>
              ))}
            </div>
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <Input
                placeholder="Buscar por cliente, serviço ou telefone..."
                value={pendingSearch}
                onChange={(e) => setPendingSearch(e.target.value)}
                className="pl-9 h-9 text-sm"
              />
            </div>
          </div>

          {unpaidRaw && unpaidRaw.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Card className="border-border/50">
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground">Total a receber</p>
                  <p className="text-2xl font-semibold text-primary mt-1">
                    R$ {unpaidRaw.reduce((sum: number, a: any) => sum + Number(a.totalPrice ?? 0), 0).toFixed(2).replace(".", ",")}
                  </p>
                </CardContent>
              </Card>
              <Card className="border-border/50">
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground">Atendimentos pendentes</p>
                  <p className="text-2xl font-semibold text-foreground mt-1">
                    {unpaid && unpaid.length !== unpaidRaw.length ? (
                      <>{unpaid.length} <span className="text-sm text-muted-foreground font-normal">de {unpaidRaw.length}</span></>
                    ) : unpaidRaw.length}
                  </p>
                </CardContent>
              </Card>
              <Card className="border-border/50">
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground">Ticket médio</p>
                  <p className="text-2xl font-semibold text-foreground mt-1">
                    R$ {unpaidRaw.length > 0 ? (unpaidRaw.reduce((sum: number, a: any) => sum + Number(a.totalPrice ?? 0), 0) / unpaidRaw.length).toFixed(2).replace(".", ",") : "0,00"}
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {nextPendingPayment && !pendingSearch.trim() && (
            <Card className="border-primary/30 bg-primary/5 md:hidden">
              <CardContent className="p-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-primary">PRÓXIMO PAGAMENTO</p>
                    <p className="truncate text-sm font-semibold text-foreground">
                      {(nextPendingPayment as any).clientName ?? (nextPendingPayment as any).userName ?? "Cliente"}
                      <span className="font-normal text-muted-foreground"> · {(nextPendingPayment as any).serviceName ?? "Serviço"}</span>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {safeFormat(nextPendingPayment.startTime, "HH:mm")} · R$ {Number((nextPendingPayment as any).totalPrice ?? 0).toFixed(2).replace(".", ",")}
                    </p>
                  </div>
                  <Button
                    onClick={() => handleOpenDialog((nextPendingPayment as any).id)}
                    className="min-h-12 shrink-0 px-4"
                  >
                    <CreditCard className="mr-2 h-4 w-4" />
                    Receber
                  </Button>
                </div>
                <p className="mt-2 text-[11px] text-muted-foreground">Abre o pagamento com o valor preenchido; confirme a forma de recebimento antes de concluir.</p>
              </CardContent>
            </Card>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : !unpaid || unpaid.length === 0 ? (
            <Card className="border-border/50">
              <CardContent className="py-16 text-center">
                {pendingSearch.trim() ? (
                  <>
                    <Search className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-lg font-medium text-foreground">Nenhum resultado</p>
                    <p className="text-sm text-muted-foreground mt-1">Nenhum atendimento encontrado para "{pendingSearch}".</p>
                    <button onClick={() => setPendingSearch("")} className="mt-3 text-xs text-primary underline">Limpar busca</button>
                  </>
                ) : (
                  <EmptyState
                    icon={CheckCircle2}
                    title="Tudo em dia!"
                    description="Não há atendimentos aguardando pagamento neste período. Você pode conferir a agenda ou registrar uma venda avulsa."
                    actionLabel="Abrir agenda"
                    onAction={() => navigate("/weekly-calendar")}
                    className="py-0"
                  />
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3">
              {unpaid.map((appt: any) => (
                <Card key={appt.id} className="border-border/50 hover:border-primary/30 transition-all">
                  <CardContent className="p-4">
                    {/* Linha 1: ícone + info do cliente/serviço */}
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <Scissors className="w-5 h-5 text-primary" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-foreground truncate">{(appt as any).serviceName ?? "Serviço"}</p>
                        <Badge variant="secondary" className="text-xs mt-0.5">
                          <Clock className="w-3 h-3 mr-1" />
                          {safeFormat(appt.startTime, "dd/MM HH:mm")}
                        </Badge>
                        <div className="flex flex-col gap-0.5 mt-1">
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            <User className="w-3 h-3 shrink-0" />
                            <span className="truncate">{(appt as any).clientName ?? (appt as any).userName ?? "Cliente"}</span>
                          </span>
                          {(appt as any).clientPhone && (
                            <span className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Phone className="w-3 h-3 shrink-0" />
                              <span>{(appt as any).clientPhone}</span>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* Linha 2: valor + botões de ação */}
                    <div className="flex items-center justify-between gap-2 pt-2 border-t border-border/30">
                      <p className="text-lg font-semibold text-primary shrink-0">
                        R$ {Number(appt.totalPrice ?? 0).toFixed(2).replace(".", ",")}
                      </p>
                      <div className="flex items-center gap-1.5">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => printComanda(appt)}
                          title="Imprimir comanda"
                          className="h-8 w-8 p-0"
                        >
                          <FileText className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleAddToQueue(appt)}
                          disabled={addToQueueMutation.isPending}
                          title="Adicionar à fila de chamada"
                          className="h-8 w-8 p-0"
                        >
                          <Users className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleOpenDialog(appt.id)}
                          className="bg-primary text-primary-foreground h-8 px-3"
                        >
                          <CreditCard className="w-4 h-4 mr-1" />
                          Receber
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ─── Aba Fila de Chamada ──────────────────────────────────────────── */}
        <TabsContent value="queue" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Gerencie a fila de atendimento. Use o{" "}
              <a href="/admin/painel-chamada" target="_blank" className="text-primary underline font-medium">
                Painel de Chamada
              </a>{" "}
              para exibir em TV.
            </p>
            {queue && queue.some((q: any) => q.status === "done") && (
              <Button size="sm" variant="outline" onClick={() => clearDone.mutate()}>
                Limpar concluídos
              </Button>
            )}
          </div>

          {overdueAssemblyItems.length > 0 && (
            <Card className="border-red-400 bg-red-50 animate-pulse">
              <CardContent className="flex items-center gap-3 p-4 text-red-900">
                <AlertCircle className="h-5 w-5 shrink-0" />
                <div>
                  <p className="font-semibold">{panelProfile.preparationTitle} acima de {panelProfile.preparationAlertMinutes} minutos</p>
                  <p className="text-xs">{overdueAssemblyItems.map((item: any) => item.clientName).join(", ")}. Verifique a responsável e atualize a fila quando a montagem estiver pronta.</p>
                </div>
              </CardContent>
            </Card>
          )}

          {!queueLoading && (!queue || queue.length === 0) && (
            <Card className="border-violet-200 bg-violet-50/50">
              <CardContent className="p-4 space-y-4">
                <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <p className="font-semibold text-violet-950">Responsável pela montagem do biquíni</p>
                    <p className="text-xs text-violet-800/80">Defina a escala antes de iniciar a fila. Qualquer colaborador ativo pode ser escolhido.</p>
                  </div>
                  <Badge className="w-fit bg-violet-600 hover:bg-violet-600">Hoje · {format(new Date(), "dd/MM")}</Badge>
                </div>
                <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
                  <div>
                    <Label htmlFor="assembly-shift-responsible-empty">Responsável ativo no turno</Label>
                    <Select value={assemblyResponsible?.responsible?.id ? String(assemblyResponsible.responsible.id) : "unassigned"} onValueChange={(value) => {
                      if (value !== "unassigned") setAssemblyShiftResponsible.mutate({ assignmentDate: assemblyAssignmentDate, staffId: Number(value) });
                    }}>
                      <SelectTrigger id="assembly-shift-responsible-empty" className="mt-1.5 bg-background"><SelectValue placeholder="Selecione um colaborador" /></SelectTrigger>
                      <SelectContent><SelectItem value="unassigned">Nenhum responsável definido</SelectItem>{(assemblyStaff as any[]).map((staff) => <SelectItem key={staff.id} value={String(staff.id)}>{staff.name} · {staff.staffRole}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <p className="text-xs text-violet-800 md:pb-2">Origem: {assemblyResponsible?.source === "turno" ? "ajuste do turno" : assemblyResponsible?.source === "escala" ? "escala semanal" : "a definir"}</p>
                </div>
                <div className="border-t border-violet-200 pt-3">
                  <p className="mb-2 text-sm font-medium text-violet-950">Escala padrão por dia da semana</p>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
                    {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map((day, dayOfWeek) => (
                      <div key={day} className="rounded-lg border border-violet-200 bg-background p-2">
                        <Label className="text-xs text-muted-foreground">{day}</Label>
                        <Select value={weeklyAssemblyByDay.get(dayOfWeek) ? String(weeklyAssemblyByDay.get(dayOfWeek)) : "unassigned"} onValueChange={(value) => {
                          if (value !== "unassigned") setAssemblyWeeklySchedule.mutate({ dayOfWeek, staffId: Number(value) });
                        }}>
                          <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue placeholder="Definir" /></SelectTrigger>
                          <SelectContent><SelectItem value="unassigned">Definir depois</SelectItem>{(assemblyStaff as any[]).map((staff) => <SelectItem key={staff.id} value={String(staff.id)}>{staff.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {queueLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          ) : !queue || queue.length === 0 ? (
            <Card className="border-border/50">
              <CardContent className="py-12 text-center">
                <Users className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">Fila vazia. Adicione clientes pelos agendamentos pendentes.</p>
              </CardContent>
            </Card>
          ) : (
            <>
              <Card className="border-violet-200 bg-violet-50/50">
                <CardContent className="p-4 space-y-4">
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <p className="font-semibold text-violet-950">Responsável pela montagem do biquíni</p>
                      <p className="text-xs text-violet-800/80">A recepção pode ajustar o responsável do turno. Qualquer colaborador ativo pode ser escolhido.</p>
                    </div>
                    <Badge className="w-fit bg-violet-600 hover:bg-violet-600">Hoje · {format(new Date(), "dd/MM")}</Badge>
                  </div>
                  <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
                    <div>
                      <Label htmlFor="assembly-shift-responsible">Responsável ativo no turno</Label>
                      <Select
                        value={assemblyResponsible?.responsible?.id ? String(assemblyResponsible.responsible.id) : "unassigned"}
                        onValueChange={(value) => {
                          if (value !== "unassigned") setAssemblyShiftResponsible.mutate({ assignmentDate: assemblyAssignmentDate, staffId: Number(value) });
                        }}
                      >
                        <SelectTrigger id="assembly-shift-responsible" className="mt-1.5 bg-background"><SelectValue placeholder="Selecione um colaborador" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unassigned">Nenhum responsável definido</SelectItem>
                          {(assemblyStaff as any[]).map((staff) => <SelectItem key={staff.id} value={String(staff.id)}>{staff.name} · {staff.staffRole}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <p className="text-xs text-violet-800 md:pb-2">Origem: {assemblyResponsible?.source === "turno" ? "ajuste do turno" : assemblyResponsible?.source === "escala" ? "escala semanal" : "a definir"}</p>
                  </div>
                  <div className="border-t border-violet-200 pt-3">
                    <p className="mb-2 text-sm font-medium text-violet-950">Escala padrão por dia da semana</p>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
                      {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map((day, dayOfWeek) => (
                        <div key={day} className="rounded-lg border border-violet-200 bg-background p-2">
                          <Label className="text-xs text-muted-foreground">{day}</Label>
                          <Select
                            value={weeklyAssemblyByDay.get(dayOfWeek) ? String(weeklyAssemblyByDay.get(dayOfWeek)) : "unassigned"}
                            onValueChange={(value) => {
                              if (value !== "unassigned") setAssemblyWeeklySchedule.mutate({ dayOfWeek, staffId: Number(value) });
                            }}
                          >
                            <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue placeholder="Definir" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="unassigned">Definir depois</SelectItem>
                              {(assemblyStaff as any[]).map((staff) => <SelectItem key={staff.id} value={String(staff.id)}>{staff.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
              <div className="space-y-2">
              {queue.map((item: any, idx: number) => {
                const preparationOverdue = item.status === "preparing" && item.preparationStartedAt && assemblyAlertClock - new Date(item.preparationStartedAt).getTime() >= panelProfile.preparationAlertMinutes * 60 * 1000;
                return (
                <Card key={item.id} className={preparationOverdue ? "border-2 border-red-500 bg-red-50/60" : "border-border/50"}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm shrink-0">
                          {idx + 1}
                        </div>
                        <div>
                          <p className="font-medium text-foreground">{item.clientName}</p>
                          <p className="text-xs text-muted-foreground">{item.serviceName} · {item.professionalName}</p>
                          {preparationOverdue && <p className="mt-1 text-xs font-semibold text-red-700">Atenção: {panelProfile.preparationTitle.toLowerCase()} ultrapassou {panelProfile.preparationAlertMinutes} minutos.</p>}
                          {(item.status === "waiting" || item.status === "preparing") && (
                            <div className="mt-2 max-w-64">
                              <Label className="text-[11px] text-muted-foreground">Responsável pela montagem</Label>
                              <Select
                                value={item.assemblyResponsibleStaffId ? String(item.assemblyResponsibleStaffId) : "turno-default"}
                                onValueChange={(value) => {
                                  if (value !== "turno-default") setQueueAssemblyResponsible.mutate({ id: item.id, staffId: Number(value) });
                                }}
                              >
                                <SelectTrigger className="mt-1 h-7 text-xs"><SelectValue placeholder="Usar responsável do turno" /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="turno-default">Usar responsável do turno</SelectItem>
                                  {(assemblyStaff as any[]).map((staff) => <SelectItem key={staff.id} value={String(staff.id)}>{staff.name} · {staff.staffRole}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1" aria-label="Alterar ordem na fila">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                            title="Subir cliente na fila"
                            aria-label="Subir cliente na fila"
                            disabled={idx === 0 || moveQueueItem.isPending}
                            onClick={() => moveQueueItem.mutate({ id: item.id, direction: "up" })}
                          >
                            <ArrowUp className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                            title="Descer cliente na fila"
                            aria-label="Descer cliente na fila"
                            disabled={idx === queue.length - 1 || moveQueueItem.isPending}
                            onClick={() => moveQueueItem.mutate({ id: item.id, direction: "down" })}
                          >
                            <ArrowDown className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                        <Badge className={`text-xs ${queueStatusColor[item.status]}`}>
                          {queueStatusLabel[item.status]}
                        </Badge>
                        {item.status === "waiting" && (
                          <Button size="sm" variant="outline" className="h-7 text-xs"
                            onClick={() => startQueuePreparation.mutate({ id: item.id, assemblyResponsibleStaffId: item.assemblyResponsibleStaffId ?? null })}>
                            {panelProfile.preparationActionLabel}
                          </Button>
                        )}
                        {item.status === "preparing" && (
                          <Button size="sm" variant="outline" className="h-7 text-xs border-violet-300 text-violet-700"
                            onClick={() => updateQueueStatus.mutate({ id: item.id, status: "called" })}>
                            {panelProfile.preparationCompleteLabel}
                          </Button>
                        )}
                        {item.status === "called" && (
                          <Button size="sm" variant="outline" className="h-7 text-xs"
                            onClick={() => updateQueueStatus.mutate({ id: item.id, status: "in_service" })}>
                            Iniciar
                          </Button>
                        )}
                        {item.status === "in_service" && (
                          <Button size="sm" variant="outline" className="h-7 text-xs"
                            onClick={() => updateQueueStatus.mutate({ id: item.id, status: "done" })}>
                            Concluir
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                          title="Alterar serviço"
                          onClick={() => { setChangeServiceItem({ id: item.id, serviceName: item.serviceName }); setNewServiceName(item.serviceName ?? ""); }}>
                          <Scissors className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive"
                          onClick={() => removeFromQueue.mutate({ id: item.id })}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                );
              })}
              </div>
            </>
          )}
        </TabsContent>

        {/* Modal: Alterar Serviço na Fila */}
        <Dialog open={!!changeServiceItem} onOpenChange={(open) => { if (!open) { setChangeServiceItem(null); setNewServiceName(""); } }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Scissors className="w-4 h-4" />
                Alterar Serviço
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <p className="text-sm text-muted-foreground">
                Serviço atual: <span className="font-medium text-foreground">{changeServiceItem?.serviceName}</span>
              </p>
              <div className="space-y-2">
                <Label>Novo serviço</Label>
                <Input
                  value={newServiceName}
                  onChange={(e) => setNewServiceName(e.target.value)}
                  placeholder="Digite ou selecione o serviço..."
                  list="service-options"
                />
                {adminServices && adminServices.length > 0 && (
                  <datalist id="service-options">
                    {adminServices.map((s: any) => (
                      <option key={s.id} value={s.name} />
                    ))}
                  </datalist>
                )}
                {adminServices && adminServices.length > 0 && (
                  <div className="grid grid-cols-2 gap-1 max-h-48 overflow-y-auto">
                    {adminServices.map((s: any) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setNewServiceName(s.name)}
                        className={`text-left text-xs px-2 py-1.5 rounded border transition-colors ${
                          newServiceName === s.name
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-muted/50 text-foreground border-border hover:bg-muted"
                        }`}
                      >
                        {s.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setChangeServiceItem(null); setNewServiceName(""); }}>Cancelar</Button>
              <Button
                disabled={!newServiceName.trim() || updateQueueServiceMutation.isPending}
                onClick={() => {
                  if (changeServiceItem && newServiceName.trim()) {
                    updateQueueServiceMutation.mutate({ id: changeServiceItem.id, serviceName: newServiceName.trim() });
                  }
                }}
              >
                {updateQueueServiceMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salvar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ─── Aba Fechamento por Turno ─────────────────────────────────────── */}
        <TabsContent value="shift" className="mt-4 space-y-4">
          <Card className="border-primary/20">
            <CardContent className="p-4 space-y-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div><p className="font-semibold">Fechamento por turno</p><p className="text-sm text-muted-foreground">Encerre o caixa atual; o próximo inicia no mesmo instante.</p></div>
                <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-800">Caixa aberto</span>
              </div>
              {currentCashShiftLoading ? <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div> : currentCashShiftError || !currentCashShift ? <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950"><p className="font-medium">Não foi possível abrir o caixa deste turno.</p><p className="mt-1 text-amber-800">Atualize a tentativa para carregar os controles de fechamento.</p><Button className="mt-3" size="sm" variant="outline" onClick={() => refetchCurrentCashShift()}>Tentar novamente</Button></div> : <>
                <div className="grid gap-3 rounded-lg bg-muted/50 p-3 sm:grid-cols-4">
                  <div><p className="text-xs text-muted-foreground">Aberto em</p><p className="font-semibold">{format(new Date(currentCashShift.periodStart), "dd/MM HH:mm")}</p></div>
                  <div><p className="text-xs text-muted-foreground">Responsável</p><p className="font-semibold truncate">{currentCashShift.openedByName ?? "Caixa"}</p></div>
                  <div><p className="text-xs text-muted-foreground">Recebimentos</p><p className="font-semibold">{shiftReport?.payments?.length ?? 0}</p></div>
                  <div><p className="text-xs text-muted-foreground">Dinheiro esperado</p><p className={`font-semibold ${shiftExpectedCashTotal >= 0 ? "text-emerald-700" : "text-red-600"}`}>R$ {shiftExpectedCashTotal.toFixed(2).replace(".", ",")}</p></div>
                </div>
                <div><p className="mb-2 text-sm font-medium">Conferência do período aberto</p><div className="grid grid-cols-2 gap-2 sm:grid-cols-3">{(shiftReport?.totalByMethod ?? []).map((item: any) => <div key={item.paymentMethod} className="rounded border bg-background p-2"><p className="text-xs text-muted-foreground">{item.paymentMethod === "dinheiro" ? "Dinheiro" : item.paymentMethod === "pix" ? "PIX" : item.paymentMethod === "credito" ? "Crédito" : item.paymentMethod === "debito" ? "Débito" : item.paymentMethod}</p><p className="font-semibold">R$ {Number(item.total ?? 0).toFixed(2)}</p></div>)}{(shiftReport?.totalByMethod?.length ?? 0) === 0 && <p className="col-span-full text-sm text-muted-foreground">Ainda não há pagamentos neste caixa.</p>}</div><div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3"><div className="flex flex-wrap items-center justify-between gap-2"><div><p className="text-sm font-medium text-emerald-950">Dinheiro físico deste turno</p><p className="text-xs text-emerald-800">Recebimentos em dinheiro + entradas manuais − saídas manuais em dinheiro.</p></div><p className={`text-lg font-bold ${shiftExpectedCashTotal >= 0 ? "text-emerald-700" : "text-red-600"}`}>R$ {shiftExpectedCashTotal.toFixed(2).replace(".", ",")}</p></div>{(shiftCashManualEntriesTotal > 0 || shiftCashExpensesTotal > 0) && <p className="mt-2 text-xs text-emerald-900">Entradas manuais em dinheiro: R$ {shiftCashManualEntriesTotal.toFixed(2).replace(".", ",")} · Saídas manuais em dinheiro: R$ {shiftCashExpensesTotal.toFixed(2).replace(".", ",")}</p>}</div></div>
                <div className="space-y-2"><Label>Período que está sendo fechado</Label><div className="flex flex-wrap gap-2">{([ ["morning", "Manhã"], ["afternoon", "Tarde"], ["custom", "Personalizado"] ] as const).map(([type, label]) => <Button key={type} type="button" size="sm" variant={shiftPeriodType === type ? "default" : "outline"} disabled={type === "afternoon" && !isAfternoonShiftAvailable} onClick={() => setShiftPeriodType(type)}>{label}</Button>)}</div>{!isAfternoonShiftAvailable && <p className="text-xs text-amber-700">O turno da tarde fica disponível após as 13h. Para fechar outro intervalo antes disso, use Personalizado.</p>}<p className="text-xs text-muted-foreground">Conferindo de {format(shiftClosingBounds.start, "dd/MM HH:mm")} até {format(shiftClosingBounds.end, "dd/MM HH:mm")}.</p>{shiftPeriodType === "custom" && <><p className="text-xs text-primary">O modo personalizado respeita exatamente as datas e horários informados abaixo.</p><div className="grid gap-2 sm:grid-cols-2"><div className="space-y-1"><Label htmlFor="shift-custom-start" className="text-xs">Início do período personalizado</Label><Input id="shift-custom-start" type="datetime-local" value={shiftCustomStart} max={shiftCustomEnd} onChange={(event) => setShiftCustomStart(event.target.value)} /></div><div className="space-y-1"><Label htmlFor="shift-custom-end" className="text-xs">Fim do período personalizado</Label><Input id="shift-custom-end" type="datetime-local" value={shiftCustomEnd} min={shiftCustomStart} max={format(new Date(), "yyyy-MM-dd'T'HH:mm")} onChange={(event) => setShiftCustomEnd(event.target.value)} /></div></div></>}</div>
                {shiftLateCashEntries.length > 0 && <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-amber-950"><div className="flex flex-wrap items-start justify-between gap-2"><div className="flex gap-2"><AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /><div><p className="text-sm font-semibold">Atendimento em data diferente</p><p className="mt-0.5 text-xs">{shiftLateCashEntries.length} lançamento(s) de atendimento anterior entraram neste caixa porque foram registrados neste período.</p></div></div><strong className="text-sm">R$ {shiftLateCashEntriesTotal.toFixed(2).replace(".", ",")}</strong></div><div className="mt-2 space-y-1 border-t border-amber-200 pt-2">{shiftLateCashEntries.map((payment: any) => <p key={payment.id} className="text-xs"><span className="font-medium">{payment.clientName ?? "Cliente"}</span> · atendimento em {format(new Date(payment.startTime), "dd/MM/yyyy HH:mm")} · lançado no caixa em {format(new Date(payment.cashEntryAt), "dd/MM/yyyy HH:mm")}</p>)}{shiftLateCashEntriesCashTotal > 0 && <p className="text-xs font-medium">R$ {shiftLateCashEntriesCashTotal.toFixed(2).replace(".", ",")} em dinheiro já recebido não entra no saldo físico esperado deste turno.</p>}</div></div>}
                <div className="space-y-1"><Label htmlFor="shift-notes">Observação do fechamento (opcional)</Label><Input id="shift-notes" value={shiftNotes} maxLength={500} onChange={(event) => setShiftNotes(event.target.value)} placeholder="Ex.: conferido com a recepção" /></div>
                <Button className="w-full bg-primary text-primary-foreground" disabled={closeCashShiftMutation.isPending || !hasValidShiftClosingBounds} onClick={() => { if (window.confirm("Encerrar este caixa? O próximo turno será aberto automaticamente.")) closeCashShiftMutation.mutate({ shiftId: currentCashShift.id, periodType: shiftPeriodType, customStart: shiftPeriodType === "custom" ? shiftClosingBounds.start : undefined, customEnd: shiftPeriodType === "custom" ? shiftClosingBounds.end : undefined, notes: shiftNotes || undefined }); }}>{closeCashShiftMutation.isPending ? "Encerrando..." : "Fechar caixa"}</Button>
              </>}
            </CardContent>
          </Card>
          <Card><CardContent className="p-4"><p className="mb-1 font-semibold">Fechamentos consultáveis</p><p className="mb-3 text-xs text-muted-foreground">Abra os detalhes para conferir recebimentos, saídas e saldo do turno sem precisar imprimir.</p>{isCashManager && <p className="mb-3 text-xs text-muted-foreground">Somente gerentes e administradores podem reabrir o último caixa, com motivo obrigatório.</p>}<div className="space-y-2">{cashShiftHistory.filter((shift: any) => shift.status === "closed").length === 0 ? <p className="text-sm text-muted-foreground">Nenhum fechamento registrado ainda.</p> : cashShiftHistory.filter((shift: any) => shift.status === "closed").map((shift: any) => <div key={shift.id} className="flex items-center justify-between gap-3 rounded border p-2"><div><p className="text-sm font-medium">{shift.periodType === "morning" ? "Manhã" : shift.periodType === "afternoon" ? "Tarde" : "Personalizado"} · {format(new Date(shift.periodStart), "dd/MM HH:mm")} — {shift.periodEnd ? format(new Date(shift.periodEnd), "HH:mm") : "-"}</p><p className="text-xs text-muted-foreground">{shift.closedByName ?? "Responsável não informado"}{shift.reopenedAt ? " · Reaberto pela gerência" : ""}</p></div><div className="flex items-center gap-2"><p className="font-semibold">R$ {Number(shift.netTotal ?? 0).toFixed(2)}</p><Button size="sm" variant="outline" onClick={() => setSelectedClosedShift(shift)}>Detalhes</Button>{isCashManager && !shift.reopenedAt && <Button size="sm" variant="outline" disabled={reopenCashShiftMutation.isPending} onClick={() => { const reason = window.prompt("Informe o motivo da reabertura do caixa:"); if (reason?.trim()) reopenCashShiftMutation.mutate({ shiftId: shift.id, reason: reason.trim() }); }}>Reabrir</Button>}</div></div>)}</div></CardContent></Card>
          <Dialog open={!!selectedClosedShift} onOpenChange={(open) => { if (!open) setSelectedClosedShift(null); }}>
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
              <DialogHeader>
                <div className="flex flex-wrap items-center justify-between gap-3 pr-8">
                  <DialogTitle>Detalhes do fechamento #{selectedClosedShift?.id}</DialogTitle>
                  {selectedClosedShift && <Button size="sm" variant="outline" onClick={() => {
                    const printed = printCashShiftClosing({
                      shift: selectedClosedShift,
                      salonName,
                      totalsByMethod: selectedShiftTotalsByMethod,
                      paymentDetails: selectedShiftPaymentDetails,
                      expenseDetails: selectedShiftExpenseDetails,
                    });
                    if (!printed) toast.error("Não foi possível abrir a janela de impressão. Verifique o bloqueio de pop-ups.");
                  }}>
                    <Printer className="mr-2 h-4 w-4" /> Reimprimir comprovante
                  </Button>}
                </div>
              </DialogHeader>
              {selectedClosedShift && <div className="space-y-4">
                <div className="rounded-lg bg-muted/60 p-3 text-sm"><div className="flex flex-wrap justify-between gap-2"><span>{selectedClosedShift.periodType === "morning" ? "Manhã" : selectedClosedShift.periodType === "afternoon" ? "Tarde" : "Personalizado"} · {format(new Date(selectedClosedShift.periodStart), "dd/MM HH:mm")} — {selectedClosedShift.periodEnd ? format(new Date(selectedClosedShift.periodEnd), "dd/MM HH:mm") : "-"}</span><strong>R$ {Number(selectedClosedShift.netTotal ?? 0).toFixed(2).replace(".", ",")}</strong></div><p className="mt-1 text-xs text-muted-foreground">Fechado por {selectedClosedShift.closedByName ?? "Responsável não informado"}</p></div>
                {!selectedClosedShift.paymentDetails && !selectedClosedShift.expenseDetails ? <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950">Este fechamento é anterior à fotografia detalhada. Os totais preservados seguem disponíveis, mas os recebimentos e saídas individuais passam a ser guardados apenas nos novos fechamentos.</div> : <>
                  <div className="grid gap-2 sm:grid-cols-3"><div className="rounded-lg border p-3"><p className="text-xs text-muted-foreground">Entradas</p><p className="font-semibold">R$ {Number(selectedClosedShift.grossTotal ?? 0).toFixed(2).replace(".", ",")}</p></div><div className="rounded-lg border p-3"><p className="text-xs text-muted-foreground">Saídas</p><p className="font-semibold text-red-600">- R$ {Number(selectedClosedShift.expenseTotal ?? 0).toFixed(2).replace(".", ",")}</p></div><div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3"><p className="text-xs text-emerald-800">Dinheiro físico</p><p className="font-semibold text-emerald-800">R$ {Number(selectedClosedShift.expectedCashTotal ?? 0).toFixed(2).replace(".", ",")}</p></div></div>
                  <div><p className="mb-2 text-sm font-semibold">Recebimentos por forma</p><div className="grid grid-cols-2 gap-2 sm:grid-cols-3">{selectedShiftTotalsByMethod.map((item: any) => <div key={item.paymentMethod} className="rounded border p-2 text-sm"><p className="text-xs text-muted-foreground">{methodLabels[item.paymentMethod] ?? item.paymentMethod}</p><p className="font-medium">R$ {Number(item.total ?? 0).toFixed(2).replace(".", ",")}</p></div>)}</div></div>
                  <div><p className="mb-2 text-sm font-semibold">Recebimentos lançados ({selectedShiftPaymentDetails.length})</p>{selectedShiftPaymentDetails.length === 0 ? <p className="rounded border border-dashed p-3 text-sm text-muted-foreground">Nenhum recebimento neste turno.</p> : <div className="space-y-2">{selectedShiftPaymentDetails.map((payment: any) => <div key={payment.id} className="rounded border p-3 text-sm"><div className="flex flex-wrap justify-between gap-2"><span className="font-medium">{payment.clientName ?? "Cliente"} · {payment.serviceName ?? "Serviço"}</span><strong>R$ {Number(payment.amount ?? 0).toFixed(2).replace(".", ",")}</strong></div><p className="mt-1 text-xs text-muted-foreground">{format(new Date(payment.occurredAt), "dd/MM HH:mm")} · {(payment.splits?.length ? payment.splits.map((split: any) => `${methodLabels[split.paymentMethod] ?? split.paymentMethod}: R$ ${Number(split.amount ?? 0).toFixed(2).replace(".", ",")}`).join(" · ") : methodLabels[payment.paymentMethod] ?? payment.paymentMethod ?? "Forma não informada")}</p></div>)}</div>}</div>
                  {selectedShiftLateCashEntries.length > 0 && <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950"><div className="flex flex-wrap items-center justify-between gap-2"><p className="font-semibold">Atendimentos lançados em data diferente ({selectedShiftLateCashEntries.length})</p><strong>R$ {selectedShiftLateCashEntriesTotal.toFixed(2).replace(".", ",")}</strong></div><div className="mt-2 space-y-1 border-t border-amber-200 pt-2">{selectedShiftLateCashEntries.map((payment: any) => <p key={payment.id} className="text-xs"><span className="font-medium">{payment.clientName ?? "Cliente"}</span> · atendimento em {payment.serviceDate ? format(new Date(payment.serviceDate), "dd/MM/yyyy HH:mm") : "data não registrada"} · lançado no caixa em {format(new Date(payment.cashEntryAt ?? payment.occurredAt), "dd/MM/yyyy HH:mm")}</p>)}{selectedShiftLateCashEntriesCashTotal > 0 && <p className="text-xs font-medium">R$ {selectedShiftLateCashEntriesCashTotal.toFixed(2).replace(".", ",")} em dinheiro já recebido não entra no saldo físico esperado deste turno.</p>}</div></div>}
                  <div>
                    <p className="mb-2 text-sm font-semibold">Movimentações manuais ({selectedShiftExpenseDetails.length})</p>
                    {selectedShiftExpenseDetails.length === 0 ? <p className="rounded border border-dashed p-3 text-sm text-muted-foreground">Nenhuma entrada ou saída manual neste turno.</p> : <div className="space-y-2">{selectedShiftExpenseDetails.map((movement: any) => {
                      const isManualIncome = movement.direction === "income";
                      return <div key={movement.id} className={`rounded border p-3 text-sm ${isManualIncome ? "border-emerald-100 bg-emerald-50/40" : "border-red-100 bg-red-50/40"}`}><div className="flex flex-wrap justify-between gap-2"><span className="font-medium">{movement.description}</span><strong className={isManualIncome ? "text-emerald-700" : "text-red-600"}>{isManualIncome ? "+" : "-"} R$ {Number(movement.amount ?? 0).toFixed(2).replace(".", ",")}</strong></div><p className="mt-1 text-xs text-muted-foreground">{format(new Date(movement.createdAt), "dd/MM HH:mm")} · {movement.paymentMethod === "dinheiro" ? isManualIncome ? "Entrada em dinheiro — somada ao saldo físico" : "Saída em dinheiro — abatida do saldo físico" : methodLabels[movement.paymentMethod] ?? movement.paymentMethod}{movement.notes ? ` · ${movement.notes}` : ""}</p></div>;
                    })}</div>}
                  </div>
                </>}
                {selectedClosedShift.notes && <div className="rounded-lg bg-muted p-3 text-sm"><span className="font-medium">Observação:</span> {selectedClosedShift.notes}</div>}
              </div>}
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* ─── Aba Relatório ────────────────────────────────────────────────── */}
        <TabsContent value="report" className="mt-4 space-y-4">
          <Card className="border-border/50">
            <CardContent className="p-4 space-y-3">
              {/* Filtros de período — recepcionista fica travado em "Hoje" */}
              <div className="flex items-center gap-2 flex-wrap">
                {(["today", "week", "month", "custom"] as PeriodPreset[])
                  .filter((p) => !isRecepcionista || p === "today")
                  .map((p: any) => (
                  <Button
                    key={p}
                    size="sm"
                    variant={periodPreset === p ? "default" : "outline"}
                    onClick={() => !isRecepcionista && setPeriodPreset(p)}
                    disabled={isRecepcionista && p !== "today"}
                    className={periodPreset === p ? "bg-primary text-primary-foreground" : ""}
                  >
                    {p === "today" ? "Hoje" : p === "week" ? "Esta semana" : p === "month" ? "Este mês" : "Personalizado"}
                  </Button>
                ))}
              </div>
              {periodPreset === "custom" && !isRecepcionista && (
                <div className="flex items-center gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">De</Label>
                    <Input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="h-8 text-sm" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Até</Label>
                    <Input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="h-8 text-sm" />
                  </div>
                </div>
              )}
              {/* Filtro de visualização */}
              <div className="flex items-center gap-2 pt-1 border-t border-border/30">
                <Filter className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Visualizar:</span>
                <Button size="sm" variant={reportView === "all" ? "default" : "outline"}
                  className={`h-7 text-xs ${reportView === "all" ? "bg-primary text-primary-foreground" : ""}`}
                  onClick={() => setReportView("all")}>
                  Todos os pagamentos
                </Button>
                <Button size="sm" variant={reportView === "discounts" ? "default" : "outline"}
                  className={`h-7 text-xs ${reportView === "discounts" ? "bg-red-600 text-white hover:bg-red-700" : "border-red-200 text-red-700 hover:bg-red-50"}`}
                  onClick={() => setReportView("discounts")}>
                  <Percent className="w-3 h-3 mr-1" />
                  Só descontos
                </Button>
                <Button size="sm" variant={reportView === "station" ? "default" : "outline"}
                  className={`h-7 text-xs ${reportView === "station" ? "bg-blue-600 text-white hover:bg-blue-700" : "border-blue-200 text-blue-700 hover:bg-blue-50"}`}
                  onClick={() => setReportView("station")}>
                  <MapPin className="w-3 h-3 mr-1" />
                  Por Sala/Estação
                </Button>
              </div>
            </CardContent>
          </Card>

          {reportLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          ) : !report || (report.payments.length === 0 && (reportExpenses ?? []).length === 0) ? (
            <Card className="border-border/50">
              <CardContent className="py-12 text-center">
                <Calendar className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">Nenhum pagamento registrado neste período.</p>
              </CardContent>
            </Card>
          ) : (
            <>
              <Card className="border-primary/30 bg-primary/5">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm text-muted-foreground">Faturamento total</p>
                      <p className="text-3xl font-semibold text-primary mt-1">
                        R$ {Number(report.grandTotal).toFixed(2).replace(".", ",")}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">{report.payments.length} pagamento(s)</p>
                        <p className="text-sm text-muted-foreground mt-0.5">
                          {report.payments.length > 0 ? `Ticket médio: R$ ${(Number(report.grandTotal) / report.payments.length).toFixed(2).replace(".", ",")}` : "Sem pagamentos neste período"}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 text-xs gap-1.5 border-primary/30 text-primary hover:bg-primary/10"
                        onClick={() => {
                          const periodLabel = periodPreset === "today" ? "Hoje" :
                            periodPreset === "week" ? "Esta semana" :
                            periodPreset === "month" ? "Este mês" :
                            `${customStart} a ${customEnd}`;
                          exportReportPDF(report, periodLabel, salonName, reportExpenses ?? []);
                        }}
                      >
                        <Download className="w-3.5 h-3.5" />
                        Exportar PDF
                      </Button>
                    </div>
                  </div>
                  <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50/70 p-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-emerald-900">Dinheiro físico esperado</p>
                        <p className="mt-0.5 text-xs text-emerald-700">Recebimentos em dinheiro + entradas manuais em dinheiro − saídas manuais em dinheiro.</p>
                      </div>
                      <p className={`text-lg font-bold ${expectedCashTotal >= 0 ? "text-emerald-700" : "text-red-600"}`}>R$ {expectedCashTotal.toFixed(2).replace(".", ",")}</p>
                    </div>
                    <p className="mt-2 text-xs text-emerald-800">Vendas em dinheiro: R$ {cashSalesTotal.toFixed(2).replace(".", ",")} · Entradas manuais: R$ {cashManualEntriesTotal.toFixed(2).replace(".", ",")} · Saídas manuais: R$ {cashExpensesTotal.toFixed(2).replace(".", ",")}</p>
                    <div className="mt-3 border-t border-emerald-200 pt-3">
                      <Label htmlFor="cash-count" className="text-xs font-medium text-emerald-900">Conferir dinheiro físico</Label>
                      <div className="mt-1.5 flex flex-col gap-2 sm:flex-row sm:items-center">
                        <Input id="cash-count" inputMode="decimal" value={cashCount} onChange={(event) => setCashCount(formatCurrencyInput(event.target.value))} placeholder="R$ 0,00" className="h-9 bg-background sm:max-w-40" />
                        {cashVariance === null ? <p className="text-xs text-emerald-800">Informe o valor contado para conferir a diferença.</p> : <Badge variant="outline" className={cashVariance === 0 ? "w-fit border-emerald-300 bg-emerald-100 text-emerald-800" : "w-fit border-amber-300 bg-amber-100 text-amber-800"}>{cashVariance === 0 ? "Caixa conferido" : `${cashVariance > 0 ? "Sobra" : "Falta"}: R$ ${Math.abs(cashVariance).toFixed(2).replace(".", ",")}`}</Badge>}
                      </div>
                      <p className="mt-1.5 text-[11px] text-emerald-800">Esta conferência não cria ou altera lançamentos.</p>
                    </div>
                  </div>
                  {reportLateCashEntries.length > 0 && <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-amber-950"><div className="flex flex-wrap items-start justify-between gap-2"><div className="flex gap-2"><AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /><div><p className="text-sm font-semibold">Atendimentos lançados em data diferente</p><p className="mt-0.5 text-xs">Estes valores compõem o período do caixa porque o pagamento foi registrado nele; confira a data real do atendimento antes de concluir a conferência.</p></div></div><strong className="text-sm">{reportLateCashEntries.length} · R$ {reportLateCashEntriesTotal.toFixed(2).replace(".", ",")}</strong></div>{reportLateCashEntriesCashTotal > 0 && <p className="mt-2 border-t border-amber-200 pt-2 text-xs font-medium">R$ {reportLateCashEntriesCashTotal.toFixed(2).replace(".", ",")} em dinheiro já recebido não entra no saldo físico esperado deste período.</p>}</div>}
                  {(totalReportManualEntries > 0 || totalReportExpenses > 0) && (
                    <div className="mt-3 pt-3 border-t border-primary/20 space-y-1.5">
                      {totalReportManualEntries > 0 && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground flex items-center gap-1.5"><ArrowUpCircle className="w-3.5 h-3.5 text-emerald-500" />Entradas manuais de caixa</span>
                          <span className="text-emerald-700 font-medium">+ R$ {totalReportManualEntries.toFixed(2).replace(".", ",")}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground flex items-center gap-1.5">
                          <ArrowDownCircle className="w-3.5 h-3.5 text-red-500" />
                          Total de saídas
                        </span>
                        <span className="text-red-600 font-medium">- R$ {totalReportExpenses.toFixed(2).replace(".", ",")}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold">Vendas menos saídas</span>
                        <span className={`text-lg font-bold ${(Number(report.grandTotal) - totalReportExpenses) >= 0 ? "text-green-600" : "text-red-600"}`}>
                          R$ {(Number(report.grandTotal) - totalReportExpenses).toFixed(2).replace(".", ",")}
                        </span>
                      </div>
                      <p className="text-[11px] text-muted-foreground">Entradas manuais são reforços ou ajustes de caixa: aparecem na conferência, mas não alteram faturamento, venda ou comissão.</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="border-border/50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Por forma de pagamento</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {report.totalByMethod.map((m: any) => (
                      <div key={m.paymentMethod ?? "null"} className={`rounded-lg p-3 ${methodColors[m.paymentMethod ?? "outro"] ?? "bg-gray-100 text-gray-700"}`}>
                        <p className="text-xs font-medium capitalize">{m.paymentMethod ?? "Não informado"}</p>
                        <p className="text-lg font-semibold mt-1">R$ {Number(m.total).toFixed(2).replace(".", ",")}</p>
                        <p className="text-xs opacity-70">{m.count} pagamento(s)</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Seção de Auditoria de Descontos */}
              {reportView === "discounts" && (() => {
                const discountPayments = report.payments.filter((p: any) => Number(p.discountAmount) > 0);
                const totalDiscounts = discountPayments.reduce((s: number, p: any) => s + Number(p.discountAmount), 0);
                return discountPayments.length === 0 ? (
                  <Card className="border-border/50">
                    <CardContent className="py-12 text-center">
                      <Percent className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                      <p className="text-sm text-muted-foreground">Nenhum desconto aplicado neste período.</p>
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    <Card className="border-red-200 bg-red-50">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-red-700 font-medium">Total de descontos concedidos</p>
                            <p className="text-2xl font-bold text-red-700 mt-1">- R$ {totalDiscounts.toFixed(2).replace(".", ",")}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-red-600">{discountPayments.length} atendimento(s) com desconto</p>
                            <p className="text-xs text-red-500 mt-1">
                              Impacto: {((totalDiscounts / Number(report.grandTotal)) * 100).toFixed(1)}% do faturamento
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border-border/50">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Percent className="w-4 h-4 text-red-600" />
                          Auditoria de Descontos
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {discountPayments.map((p: any) => (
                            <div key={p.id} className="border border-red-100 rounded-lg p-3 bg-red-50/40 space-y-2">
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <p className="text-sm font-medium truncate">{p.clientName ?? "Cliente"}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {safeFormat(p.startTime, "dd/MM/yyyy HH:mm")}
                                    <span className="ml-2">· {p.serviceName ?? "Serviço"}</span>
                                  </p>
                                </div>
                                <div className="text-right shrink-0">
                                  <p className="text-xs text-muted-foreground line-through">R$ {Number(p.totalPrice).toFixed(2)}</p>
                                  <p className="text-sm font-semibold text-red-700">- R$ {Number(p.discountAmount).toFixed(2)}</p>
                                </div>
                              </div>
                              <div className="flex flex-wrap gap-1.5 pt-1 border-t border-red-100">
                                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">
                                  <Percent className="w-3 h-3" />
                                  Motivo: {p.discountReason || "Não informado"}
                                </span>
                                {p.splits && p.splits.map((s: any, si: number) => (
                                  <span key={si} className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${methodColors[s.paymentMethod] ?? "bg-gray-100 text-gray-700"}`}>
                                    {methodLabels[s.paymentMethod] ?? s.paymentMethod}
                                    <span className="opacity-80">R$ {Number(s.amount).toFixed(2)}</span>
                                  </span>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </>
                );
              })()}

              {/* Agrupamento por Sala/Estação (view = station) */}
              {reportView === "station" && (() => {
                // Agrupa pagamentos por sala/estação do profissional
                const stationMap = new Map<string, { payments: any[]; total: number }>();
                report.payments.forEach((p: any) => {
                  const key = p.sourceType === "standalone_sale"
                    ? "Vendas avulsas"
                    : (p as any).stationName || (p as any).professionalName || "Sem Sala";
                  if (!stationMap.has(key)) stationMap.set(key, { payments: [], total: 0 });
                  const entry = stationMap.get(key)!;
                  entry.payments.push(p);
                  entry.total += Number(p.totalPrice) - Number(p.discountAmount ?? 0);
                });
                const stations = Array.from(stationMap.entries()).sort((a, b) => b[1].total - a[1].total);
                return stations.length === 0 ? null : (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-blue-600" />
                      <h3 className="text-sm font-semibold text-foreground">Faturamento por Sala / Estação</h3>
                    </div>
                    {stations.map(([station, data]) => (
                      <Card key={station} className="border-blue-200/50">
                        <CardHeader className="pb-2 pt-3 px-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <MapPin className="w-3.5 h-3.5 text-blue-500" />
                              <span className="font-semibold text-sm">{station}</span>
                              <Badge variant="outline" className="text-xs border-blue-200 text-blue-700">
                                {data.payments.length} atend.
                              </Badge>
                            </div>
                            <span className="text-base font-bold text-blue-700">
                              R$ {data.total.toFixed(2).replace(".", ",")}
                            </span>
                          </div>
                        </CardHeader>
                        <CardContent className="px-4 pb-3">
                          <div className="space-y-2">
                            {data.payments.map((p: any) => (
                              <div key={p.id} className="flex items-center justify-between text-xs py-1.5 border-b border-border/20 last:border-0">
                                <div className="min-w-0 flex-1">
                                  <span className="font-medium truncate block">{p.clientName ?? "Cliente"}</span>
                                  <span className="text-muted-foreground">{safeFormat(p.startTime, "dd/MM HH:mm")} · {p.serviceName}</span>
                                </div>
                                <div className="text-right shrink-0 ml-3">
                                  <span className="font-semibold text-primary">
                                    R$ {(Number(p.totalPrice) - Number(p.discountAmount ?? 0)).toFixed(2).replace(".", ",")}
                                  </span>
                                  {Number(p.discountAmount) > 0 && (
                                    <span className="block text-red-500">
                                      -{Number(p.discountAmount).toFixed(2).replace(".", ",")}
                                    </span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                );
              })()}

              {/* Detalhamento completo (view = all) */}
              {reportView === "all" && (
                <Card className="border-border/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Detalhamento</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {report.payments.map((p: any) => (
                        <div key={p.id} className="border border-border/30 rounded-lg p-3 space-y-2">
                          {/* Linha principal */}
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{p.clientName ?? "Cliente"}</p>
                              <p className="text-xs text-muted-foreground">
                                {safeFormat(p.startTime, "dd/MM/yyyy HH:mm")}
                                {p.clientPhone && <span className="ml-2">· {p.clientPhone}</span>}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className="text-sm font-semibold text-primary">
                                R$ {Number(p.totalPrice).toFixed(2).replace(".", ",")}
                              </span>
                              {p.sourceType !== "standalone_sale" && p.paymentStatus !== "refunded" && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 px-2 text-xs text-destructive border-destructive/30 hover:bg-destructive/10"
                                  onClick={() => { setRefundTargetId(p.id); setRefundPinOpen(true); }}
                                  title="Estornar pagamento"
                                >
                                  <RotateCcw className="w-3 h-3 mr-1" />
                                  Estornar
                                </Button>
                              )}
                              {p.paymentStatus === "refunded" && (
                                <Badge className="text-xs bg-red-100 text-red-700">Estornado</Badge>
                              )}
                            </div>
                          </div>

                          {p.isLateCashEntry && <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-medium"><AlertCircle className="w-3 h-3" />Atendimento em {safeFormat(p.startTime, "dd/MM")} · lançado no caixa em {safeFormat(p.cashEntryAt, "dd/MM")}</span>}
                          {/* Splits de pagamento */}
                          {p.splits && p.splits.length > 0 ? (
                          <div className="flex flex-wrap gap-1.5 pt-1 border-t border-border/20">
                            {p.sourceType === "standalone_sale" && (
                              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-medium">
                                <Package className="w-3 h-3" />
                                Venda avulsa
                              </span>
                            )}
                            {p.splits.map((s: any, si: number) => (
                                <span key={si} className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${methodColors[s.paymentMethod] ?? "bg-gray-100 text-gray-700"}`}>
                                  {methodLabels[s.paymentMethod] ?? s.paymentMethod}
                                  <span className="opacity-80">R$ {Number(s.amount).toFixed(2)}</span>
                                </span>
                              ))}
                              {Number(p.discountAmount) > 0 && (
                                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">
                                  <Percent className="w-3 h-3" />
                                  Desconto R$ {Number(p.discountAmount).toFixed(2)}
                                  {p.discountReason && <span className="opacity-70">· {p.discountReason}</span>}
                                </span>
                              )}
                            </div>
                          ) : p.paymentMethod ? (
                            <div className="pt-1 border-t border-border/20">
                              <Badge className={`text-xs ${methodColors[p.paymentMethod] ?? "bg-gray-100 text-gray-700"}`}>
                                {methodLabels[p.paymentMethod] ?? p.paymentMethod}
                              </Badge>
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* ─── Histórico de Vendas Avulsas de Produtos ─── */}
          <Card className="border-border/50 mt-2">
            <CardHeader className="pb-2 pt-4 px-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Package className="w-4 h-4 text-primary" />
                  Vendas Avulsas de Produtos
                </CardTitle>
                {standaloneSales && standaloneSales.length > 0 && (
                  <div className="flex items-center gap-1.5">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs gap-1 border-border/50"
                      onClick={() => {
                        const header = "Data,Produto,Categoria,Qtd,Preço Unit.,Total,Observações";
                        const rows = standaloneSales.map(s => [
                          new Date(Number(s.createdAt)).toLocaleString("pt-BR"),
                          `"${s.productName}"`,
                          s.productCategory ?? "",
                          Math.abs(Number(s.qty)),
                          Number(s.unitPrice || 0).toFixed(2),
                          (Math.abs(Number(s.qty)) * Number(s.unitPrice || 0)).toFixed(2),
                          `"${s.notes ?? ""}"`
                        ].join(","));
                        const csv = [header, ...rows].join("\n");
                        const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url; a.download = `vendas-avulsas-${format(new Date(), "yyyy-MM-dd")}.csv`;
                        a.click(); URL.revokeObjectURL(url);
                      }}
                    >
                      <Download className="w-3 h-3" /> CSV
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs gap-1 border-border/50"
                      onClick={() => {
                        const win = window.open("", "_blank");
                        if (!win) return;
                        const total = standaloneSales.reduce((sum, s) => sum + Math.abs(Number(s.qty)) * Number(s.unitPrice || 0), 0);
                        const rows = standaloneSales.map(s => `
                          <tr>
                            <td>${new Date(Number(s.createdAt)).toLocaleString("pt-BR")}</td>
                            <td>${s.productName}</td>
                            <td>${s.productCategory ?? "-"}</td>
                            <td style="text-align:center">${Math.abs(Number(s.qty))}</td>
                            <td style="text-align:right">R$ ${Number(s.unitPrice || 0).toFixed(2)}</td>
                            <td style="text-align:right">R$ ${(Math.abs(Number(s.qty)) * Number(s.unitPrice || 0)).toFixed(2)}</td>
                            <td>${s.notes ?? ""}</td>
                          </tr>`).join("");
                        win.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Vendas Avulsas</title>
                          <style>body{font-family:Arial,sans-serif;font-size:12px;padding:20px}h2{margin-bottom:4px}table{width:100%;border-collapse:collapse;margin-top:12px}th,td{border:1px solid #ddd;padding:6px 8px;text-align:left}th{background:#f5f5f5;font-weight:600}.total{text-align:right;font-weight:bold;margin-top:12px;font-size:14px}</style></head>
                          <body><h2>${salonName}</h2><p>Vendas Avulsas de Produtos</p>
                          <table><thead><tr><th>Data/Hora</th><th>Produto</th><th>Categoria</th><th>Qtd</th><th>Preço Unit.</th><th>Total</th><th>Observações</th></tr></thead><tbody>${rows}</tbody></table>
                          <p class="total">Total: R$ ${total.toFixed(2).replace(".", ",")}</p></body></html>`);
                        win.document.close(); win.focus(); setTimeout(() => { win.print(); win.close(); }, 400);
                      }}
                    >
                      <Printer className="w-3 h-3" /> PDF
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              {standaloneSalesLoading ? (
                <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
              ) : !standaloneSales || standaloneSales.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">Nenhuma venda avulsa neste período.</p>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs text-muted-foreground">{standaloneSales.length} venda(s)</span>
                    <span className="text-sm font-semibold text-primary">
                      Total: R$ {standaloneSales.reduce((sum, s) => sum + Math.abs(Number(s.qty)) * (Number(s.unitPrice) || 0), 0).toFixed(2).replace(".", ",")}
                    </span>
                  </div>
                  {standaloneSales.map((s: any) => (
                    <div key={s.id} className="flex items-start justify-between gap-2 p-2.5 rounded-lg bg-muted/30 border border-border/30">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{s.productName}</p>
                        {s.notes && <p className="text-xs text-muted-foreground truncate">{s.notes}</p>}
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {new Date(Number(s.createdAt)).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-semibold">R$ {(Math.abs(Number(s.qty)) * (Number(s.unitPrice) || 0)).toFixed(2).replace(".", ",")}</p>
                        <p className="text-xs text-muted-foreground">{Math.abs(Number(s.qty))} {s.productCategory ? `· ${s.productCategory}` : ""}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Aba Venda Avulsa ──────────────────────────────────────────────── */}
        <TabsContent value="sell" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Coluna esquerda: busca e lista de produtos */}
            <div className="space-y-4">
              <Card className="border-border/50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Package className="w-4 h-4 text-primary" />
                    Selecionar Produtos
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar produto..."
                      value={saleProductSearch}
                      onChange={(e) => setSaleProductSearch(e.target.value)}
                      className="pl-9 h-9 text-sm"
                    />
                  </div>
                  <div className="max-h-64 overflow-y-auto space-y-1 pr-1">
                    {filteredSaleProducts.length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-4">Nenhum produto com estoque disponível.</p>
                    )}
                    {filteredSaleProducts.map((p: any) => {
                      const inCart = saleProducts.find(sp => sp.productId === p.id);
                      return (
                        <div
                          key={p.id}
                          className={`flex items-center gap-2.5 p-2.5 rounded-lg border text-sm cursor-pointer transition-colors ${
                            inCart ? "border-primary/50 bg-primary/5" : "border-border/50 hover:border-primary/30 hover:bg-muted/30"
                          }`}
                          onClick={() => {
                            if (!inCart) {
                              setSaleProducts(prev => [...prev, {
                                productId: p.id,
                                name: p.name,
                                quantity: 1,
                                unitPrice: Number(p.salePrice),
                              }]);
                            }
                          }}
                        >
                          {/* Miniatura da foto */}
                          {(p as any).imageUrl ? (
                            <img src={(p as any).imageUrl} alt={p.name} className="w-10 h-10 rounded-md object-cover border border-border/40 shrink-0" />
                          ) : (
                            <div className="w-10 h-10 rounded-md bg-muted/50 border border-border/30 flex items-center justify-center shrink-0">
                              <Package className="w-4 h-4 text-muted-foreground" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{p.name}</p>
                            <p className="text-xs text-muted-foreground">{p.brand ? `${p.brand} · ` : ""}{p.category ?? ""} · Estoque: {Number(p.stockQty).toFixed(0)} {p.unit ?? "un"}</p>
                          </div>
                          <div className="flex items-center gap-2 ml-2">
                            <span className="font-semibold text-primary whitespace-nowrap">R$ {Number(p.salePrice).toFixed(2)}</span>
                            {inCart ? (
                              <Badge className="text-xs bg-primary/10 text-primary border-primary/20">No carrinho</Badge>
                            ) : (
                              <Button size="sm" variant="outline" className="h-7 px-2 text-xs">
                                <Plus className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Carrinho */}
              {saleProducts.length > 0 && (
                <Card className="border-border/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Carrinho ({saleProducts.length} item{saleProducts.length > 1 ? "s" : ""})</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {saleProducts.map((sp, idx) => (
                      <div key={sp.productId} className="flex items-center gap-2 p-2 rounded-lg bg-muted/30">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{sp.name}</p>
                          <p className="text-xs text-muted-foreground">R$ {sp.unitPrice.toFixed(2)}/un</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="outline" className="h-6 w-6 p-0" onClick={() => {
                            if (sp.quantity <= 1) setSaleProducts(prev => prev.filter((_, i) => i !== idx));
                            else setSaleProducts(prev => prev.map((p, i) => i === idx ? { ...p, quantity: p.quantity - 1 } : p));
                          }}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                          <span className="text-sm w-6 text-center font-medium">{sp.quantity}</span>
                          <Button size="sm" variant="outline" className="h-6 w-6 p-0" onClick={() => {
                            setSaleProducts(prev => prev.map((p, i) => i === idx ? { ...p, quantity: p.quantity + 1 } : p));
                          }}>
                            <Plus className="w-3 h-3" />
                          </Button>
                        </div>
                        <span className="text-sm font-semibold w-20 text-right">R$ {(sp.quantity * sp.unitPrice).toFixed(2)}</span>
                      </div>
                    ))}
                    {!isProfessionalConsumption && (
                      <div className="flex items-center gap-2 pt-1">
                        <Label className="text-xs text-muted-foreground">Desconto (R$)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={saleDiscountAmount}
                          onChange={(e) => setSaleDiscountAmount(e.target.value)}
                          className="h-7 text-sm w-24"
                        />
                      </div>
                    )}
                    <div className="flex justify-between items-center pt-1 border-t border-border/30">
                      <span className="text-sm font-semibold">{isProfessionalConsumption ? "Débito da comissão" : "Total"}</span>
                      <span className="text-base font-bold text-primary">R$ {(isProfessionalConsumption ? saleProfessionalConsumptionTotal : saleFinalTotal).toFixed(2)}</span>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Coluna direita: cliente e pagamento */}
            <div className="space-y-4">
              <Card className="border-border/50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <User className="w-4 h-4 text-primary" />
                    Cliente ou Profissional (opcional)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="relative">
                    <Input
                      placeholder="Buscar cliente ou profissional..."
                      value={saleRecipientSearch}
                      onChange={(e) => handleSaleRecipientChange(e.target.value)}
                      onBlur={() => setTimeout(() => setShowClientSuggestions(false), 150)}
                      onFocus={() => setShowClientSuggestions(saleRecipientSearch.trim().length >= 2 && saleRecipientSuggestions.length > 0)}
                      className="h-9 text-sm"
                    />
                    {showClientSuggestions && (
                      <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-md overflow-hidden">
                        {saleRecipientSuggestions.map((recipient, i) => (
                          <button
                            key={`${recipient.type}-${recipient.id}-${i}`}
                            type="button"
                            className="w-full text-left px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground flex items-center gap-2"
                            onMouseDown={() => {
                              setSaleRecipientSearch(recipient.name);
                              setSaleClientName(recipient.type === "client" ? recipient.name : "");
                              setSaleProfessional(recipient.type === "professional" ? { id: recipient.id, name: recipient.name } : null);
                              if (recipient.type === "professional") {
                                setSaleDiscountAmount("0");
                                setSaleSplits([]);
                                setSaleCashReceived("");
                              } else if (saleSplits.length === 0) {
                                setSaleSplits([{ method: "dinheiro", amount: saleFinalTotal.toFixed(2) }]);
                              }
                              setShowClientSuggestions(false);
                            }}
                          >
                            {recipient.type === "professional" ? <Users className="w-3.5 h-3.5 text-amber-700 shrink-0" /> : <User className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                            <span className="truncate">{recipient.name}</span>
                            <span className="text-xs text-muted-foreground ml-auto shrink-0">{recipient.detail}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {saleBranches.length > 0 && <div className="mt-3 space-y-1.5"><Label className="text-xs">Filial da operação</Label><Select value={saleBranchId} onValueChange={setSaleBranchId}><SelectTrigger className="h-9"><SelectValue placeholder="Selecione a filial" /></SelectTrigger><SelectContent>{saleBranches.map((branch: any) => <SelectItem key={branch.id} value={String(branch.id)}>{branch.name}</SelectItem>)}</SelectContent></Select><p className="text-xs text-muted-foreground">A filial é lembrada neste dispositivo e atribui automaticamente o custo do produto à rentabilidade da unidade.</p></div>}
                    {isProfessionalConsumption && (
                      <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                        <span className="font-semibold">Consumo de {saleProfessional.name}.</span> O estoque será abatido e o valor será descontado da comissão, sem entrada no Caixa.
                        {saleProfessionalProductsWithoutCost.length > 0 && (
                          <p className="mt-1 text-xs text-rose-700">Configure o custo de uso profissional ou o custo do produto para: {saleProfessionalProductsWithoutCost.map((item) => item.name).join(", ")}.</p>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className={isProfessionalConsumption ? "border-amber-200 bg-amber-50/40" : "border-border/50"}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    {isProfessionalConsumption ? <ArrowDownCircle className="w-4 h-4 text-amber-700" /> : <CreditCard className="w-4 h-4 text-primary" />}
                    {isProfessionalConsumption ? "Débito na Comissão" : "Forma de Pagamento"}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {isProfessionalConsumption ? (
                    <div className="space-y-2 text-sm">
                      <p className="text-muted-foreground">Nenhum pagamento será recebido. O valor abaixo aparecerá em <strong>Produtos usados</strong> no período de comissão.</p>
                      <div className="flex items-center justify-between rounded-lg bg-background border border-amber-200 px-3 py-2">
                        <span className="font-medium">Consumo de produtos</span>
                        <span className="font-bold text-red-600">- R$ {saleProfessionalConsumptionTotal.toFixed(2)}</span>
                      </div>
                    </div>
                  ) : saleSplits.map((split, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div className="grid grid-cols-3 gap-1 flex-1 min-w-0">
                        {paymentMethods.map((m: any) => (
                          <button
                            key={m.value}
                            type="button"
                            onClick={() => setSaleSplits(prev => prev.map((s, i) => i === idx ? { ...s, method: m.value } : s))}
                            className={`flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                              split.method === m.value ? m.color + " ring-1 ring-current" : "border-border/50 hover:border-primary/30"
                            }`}
                          >
                            {m.icon}
                            <span className="hidden sm:inline">{m.label}</span>
                          </button>
                        ))}
                      </div>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="R$"
                        value={split.amount}
                        onChange={(e) => setSaleSplits(prev => prev.map((s, i) => i === idx ? { ...s, amount: e.target.value } : s))}
                        className="h-9 text-sm w-28"
                      />
                      {idx > 0 && (
                        <Button size="sm" variant="ghost" className="h-9 w-9 p-0 text-destructive" onClick={() => setSaleSplits(prev => prev.filter((_, i) => i !== idx))}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  {!isProfessionalConsumption && <Button
                    size="sm"
                    variant="outline"
                    className="w-full h-8 text-xs"
                    onClick={() => setSaleSplits(prev => [...prev, { method: "pix", amount: saleRemaining > 0 ? saleRemaining.toFixed(2) : "" }])}
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Adicionar forma de pagamento
                  </Button>}

                  {/* Troco */}
                  {!isProfessionalConsumption && saleHasSingleCashSplit && (
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-muted-foreground whitespace-nowrap">Valor recebido</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="R$"
                        value={saleCashReceived}
                        onChange={(e) => setSaleCashReceived(e.target.value)}
                        className="h-8 text-sm"
                      />
                      {saleTroco > 0 && (
                        <span className="text-sm font-bold text-green-600 whitespace-nowrap">Troco: R$ {saleTroco.toFixed(2)}</span>
                      )}
                    </div>
                  )}

                  {/* Status do pagamento */}
                  <div className={`rounded-lg p-3 text-sm flex justify-between items-center ${
                    saleProducts.length === 0 ? "bg-muted/30 text-muted-foreground" :
                    isProfessionalConsumption ? "bg-amber-100 text-amber-900" :
                    saleIsBalanced ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"
                  }`}>
                    <span>
                      {saleProducts.length === 0 ? "Adicione produtos ao carrinho" :
                       isProfessionalConsumption ? "Pronto para lançar o débito de consumo" :
                       saleIsBalanced ? "Pagamento conferido" : `Faltam R$ ${saleRemaining.toFixed(2)}`}
                    </span>
                    {(isProfessionalConsumption || saleIsBalanced) && saleProducts.length > 0 && <CheckCircle2 className="w-4 h-4" />}
                  </div>

                  <Button
                    className="w-full"
                    disabled={saleActionDisabled}
                    onClick={openSaleReview}
                  >
                    {sellProductsMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Receipt className="w-4 h-4 mr-2" />
                    )}
                    {isProfessionalConsumption ? "Revisar consumo na comissão" : "Revisar venda"}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
        {/* ─── Aba Saídas de Caixa ──────────────────────────────────────────── */}
        <TabsContent value="expenses" className="mt-4">
          <ExpensesTab />
        </TabsContent>
      </Tabs>

      <MobileSaleActionBar visible={activeTab === "sell" && saleProducts.length > 0} disabled={saleActionDisabled} pending={sellProductsMutation.isPending} onSubmit={openSaleReview} label={isProfessionalConsumption ? `Revisar consumo · R$ ${saleProfessionalConsumptionTotal.toFixed(2)}` : `Revisar venda · R$ ${saleFinalTotal.toFixed(2)}`} />

      {/* ─── Modal de PIN para estorno ──────────────────────────────────────── */}
      <AuthPinModal
        open={refundPinOpen}
        onClose={() => { setRefundPinOpen(false); setRefundTargetId(null); }}
        title="Estornar Pagamento"
        description="Esta ação requer autorização. Digite o PIN de administrador para estornar este pagamento."
        onConfirm={async (pin) => {
          if (!refundTargetId) return;
          await refundWithAuth.mutateAsync({ appointmentId: refundTargetId, pin });
        }}
      />

      {/* ─── Modal de PIN para desconto ─────────────────────────────────────── */}
      <AuthPinModal
        open={discountPinOpen}
        onClose={() => setDiscountPinOpen(false)}
        title="Autorizar Desconto"
        description={`Desconto de R$ ${Number(pendingDiscount?.amount ?? 0).toFixed(2)}. Digite o PIN de administrador para autorizar.`}
        onConfirm={async (pin) => {
          if (!selectedAppointment || !pendingDiscount) return;
          await applyDiscountWithPin.mutateAsync({
            appointmentId: selectedAppointment,
            discountAmount: Number(pendingDiscount.amount),
            discountReason: pendingDiscount.reason,
            pin,
          });
          setDiscountAmount(pendingDiscount.amount);
          setDiscountReason(pendingDiscount.reason);
        }}
      />

      {/* ─── Modal de Pagamento ─────────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) setPaymentReviewOpen(false);
      }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-[Cormorant_Garamond,serif] text-xl">Registrar Pagamento</DialogTitle>
          </DialogHeader>

          {selectedAppt && (
            <div className="space-y-4">
              {/* Resumo do atendimento */}
              <Card className="border-border/50 bg-secondary/30">
                <CardContent className="p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Cliente</span>
                    <span className="font-medium">{(selectedAppt as any).clientName ?? (selectedAppt as any).userName ?? "Cliente"}</span>
                  </div>
                  {/* Lista discriminada de serviços com profissional e valor individual */}
                  {commandaServices.length > 0 ? (
                    <div className="space-y-1.5 border-t border-border/30 pt-2">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Serviços</p>
                      {commandaServices.map((s: any, idx: number) => (
                        <div key={idx} className="flex justify-between items-start text-sm gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-foreground leading-tight">{s.serviceName}</p>
                            {s.professionalName && (
                              <p className="text-xs text-muted-foreground">{s.professionalName}</p>
                            )}
                          </div>
                          <span className="font-semibold text-foreground shrink-0">R$ {Number(s.price).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Profissional</span>
                      <span className="font-medium">{(selectedAppt as any).professionalName ?? "-"}</span>
                    </div>
                  )}
                  {(selectedAppt as any).clientPhone && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground flex items-center gap-1">
                        <MessageCircle className="w-3 h-3 text-green-500" />WhatsApp
                      </span>
                      <span className="font-medium text-green-600">{(selectedAppt as any).clientPhone}</span>
                    </div>
                  )}
                  {/* Campo de valor cobrado ("a partir de") */}
                  <div className="border border-border/50 rounded-lg p-3 space-y-1 bg-secondary/20">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-medium flex items-center gap-1">
                        <TrendingUp className="w-3.5 h-3.5 text-primary" />
                        Valor cobrado
                      </Label>
                      <span className="text-xs text-muted-foreground">
                        A partir de R$ {baseServicePrice.toFixed(2)}
                      </span>
                    </div>
                    <Input
                      type="number"
                      min={baseServicePrice}
                      step="0.01"
                      value={customPrice}
                      onChange={(e) => {
                        const val = e.target.value;
                        setCustomPrice(val);
                        // Se o valor digitado for menor que o mínimo, acionar desconto com PIN
                        const numVal = Number(val);
                        if (val !== "" && numVal < baseServicePrice) {
                          const diff = (baseServicePrice - numVal).toFixed(2);
                          setDiscountAmount(diff);
                          setDiscountReason("Ajuste de valor");
                        } else {
                          // Limpar desconto se o valor voltou ao normal
                          if (!discountApproved) {
                            setDiscountAmount("0");
                            setDiscountReason("");
                          }
                        }
                      }}
                      placeholder={baseServicePrice.toFixed(2)}
                      className="h-8 text-sm"
                    />
                    {customPrice !== "" && Number(customPrice) > baseServicePrice && (
                      <p className="text-xs text-green-600 flex items-center gap-1">
                        <TrendingUp className="w-3 h-3" />
                        +R$ {(Number(customPrice) - baseServicePrice).toFixed(2)} acima do preço base
                      </p>
                    )}
                    {customPrice !== "" && Number(customPrice) < baseServicePrice && (
                      <p className="text-xs text-amber-600 flex items-center gap-1">
                        Valor abaixo do mínimo — requer autorização PIN
                      </p>
                    )}
                  </div>
                  <div className="flex justify-between text-sm border-t border-border/50 pt-2 mt-2">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="font-semibold">R$ {totalPrice.toFixed(2)}</span>
                  </div>
                  {discountApproved && discountVal > 0 && (
                    <div className="flex justify-between text-sm text-red-600">
                      <span>Desconto autorizado</span>
                      <span>- R$ {discountVal.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-base border-t border-border/50 pt-2 mt-2">
                    <span className="font-medium">Total a pagar</span>
                    <span className="text-lg font-semibold text-primary">R$ {finalTotal.toFixed(2)}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Produtos adicionais */}
              <div className="border border-border/50 rounded-lg p-3 space-y-2">
                <p className="text-sm font-medium flex items-center gap-2">
                  <Package className="w-4 h-4 text-primary" />
                  Produtos Adicionais
                </p>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-muted-foreground" />
                  <Input
                    placeholder="Buscar produto..."
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    className="h-8 text-sm pl-8"
                  />
                </div>
                {productSearch && filteredProducts.length > 0 && (
                  <div className="border border-border/50 rounded-md max-h-32 overflow-y-auto bg-background shadow-sm">
                    {filteredProducts.slice(0, 6).map(p => (
                      <button
                        key={p.id}
                        className="w-full text-left px-3 py-2 text-xs hover:bg-secondary/50 flex justify-between items-center border-b border-border/30 last:border-0"
                        onClick={() => {
                          const existing = checkoutProducts.find(cp => cp.productId === p.id);
                          if (existing) {
                            setCheckoutProducts(prev => prev.map(cp => cp.productId === p.id ? { ...cp, quantity: cp.quantity + 1 } : cp));
                          } else {
                            setCheckoutProducts(prev => [...prev, { productId: p.id, name: p.name, quantity: 1, unitPrice: Number(p.salePrice) }]);
                          }
                          setProductSearch("");
                        }}
                      >
                        <span className="font-medium">{p.name}</span>
                        <span className="text-primary font-semibold">R$ {Number(p.salePrice).toFixed(2)}</span>
                      </button>
                    ))}
                  </div>
                )}
                {checkoutProducts.length > 0 && (
                  <div className="space-y-1">
                    {checkoutProducts.map((cp, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-xs">
                        <span className="flex-1 truncate">{cp.name}</span>
                        <div className="flex items-center gap-1">
                          <button className="w-5 h-5 rounded border flex items-center justify-center hover:bg-secondary" onClick={() => setCheckoutProducts(prev => prev.map((p, i) => i === idx ? { ...p, quantity: Math.max(1, p.quantity - 1) } : p))}>
                            -
                          </button>
                          <span className="w-6 text-center font-medium">{cp.quantity}</span>
                          <button className="w-5 h-5 rounded border flex items-center justify-center hover:bg-secondary" onClick={() => setCheckoutProducts(prev => prev.map((p, i) => i === idx ? { ...p, quantity: p.quantity + 1 } : p))}>
                            +
                          </button>
                        </div>
                        <span className="w-16 text-right font-semibold text-primary">R$ {(cp.quantity * cp.unitPrice).toFixed(2)}</span>
                        <button className="text-destructive hover:text-destructive/80" onClick={() => setCheckoutProducts(prev => prev.filter((_, i) => i !== idx))}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    <div className="flex justify-between text-xs font-semibold border-t border-border/30 pt-1 mt-1">
                      <span>Subtotal produtos</span>
                      <span className="text-primary">R$ {productsTotal.toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Produtos Utilizados no Serviço */}
              <div className="border border-amber-500/30 rounded-lg p-3 space-y-2 bg-amber-500/5">
                <p className="text-sm font-medium flex items-center gap-2 text-amber-700 dark:text-amber-400">
                  <FlaskConical className="w-4 h-4" />
                  Produtos Utilizados no Serviço
                  <span className="text-xs font-normal text-muted-foreground ml-1">(desconta da comissão)</span>
                </p>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Buscar produto interno..."
                    value={usedProductSearch}
                    onChange={(e) => setUsedProductSearch(e.target.value)}
                    className="h-8 text-sm pl-8 w-full rounded-md border border-input bg-background px-3 py-1 shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>
                {usedProductSearch && filteredUsedProducts.length > 0 && (
                  <div className="border border-border/50 rounded-md max-h-36 overflow-y-auto bg-background shadow-sm">
                    {filteredUsedProducts.slice(0, 8).map(p => (
                      <div key={p.id} className="px-3 py-2 text-xs border-b border-border/30 last:border-0">
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-medium">{p.name}</span>
                          <span className="text-muted-foreground">{p.unit ?? "un"}</span>
                        </div>
                        <div className="flex gap-2">
                          <button
                            className="flex-1 rounded border border-amber-400/50 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 px-2 py-1 text-xs hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors"
                            onClick={() => {
                              const unitCost = parseFloat(p.internalUseCost ?? p.costPrice);
                              const existing = usedProducts.find(up => up.productId === p.id && up.usageType === "internal_service");
                              if (existing) {
                                setUsedProducts(prev => prev.map(up => up.productId === p.id && up.usageType === "internal_service" ? { ...up, qty: up.qty + 1 } : up));
                              } else {
                                setUsedProducts(prev => [...prev, { productId: p.id, name: p.name, qty: 1, usageType: "internal_service", unit: p.unit ?? "un", unitCost, notes: "" }]);
                              }
                              setUsedProductSearch("");
                            }}
                          >
                            ✂️ Uso Interno — R$ {parseFloat(p.internalUseCost ?? p.costPrice).toFixed(2)}
                          </button>
                          <button
                            className="flex-1 rounded border border-blue-400/50 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 px-2 py-1 text-xs hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
                            onClick={() => {
                              const unitCost = parseFloat(p.professionalUseCost ?? p.costPrice);
                              const existing = usedProducts.find(up => up.productId === p.id && up.usageType === "professional_own");
                              if (existing) {
                                setUsedProducts(prev => prev.map(up => up.productId === p.id && up.usageType === "professional_own" ? { ...up, qty: up.qty + 1 } : up));
                              } else {
                                setUsedProducts(prev => [...prev, { productId: p.id, name: p.name, qty: 1, usageType: "professional_own", unit: p.unit ?? "un", unitCost, notes: "" }]);
                              }
                              setUsedProductSearch("");
                            }}
                          >
                            👤 Uso Próprio — R$ {parseFloat(p.professionalUseCost ?? p.costPrice).toFixed(2)}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {usedProductSearch && filteredUsedProducts.length === 0 && (
                  <p className="text-xs text-muted-foreground px-1">Nenhum produto com desconto de comissão encontrado.</p>
                )}
                {usedProducts.length > 0 && (
                  <div className="space-y-1">
                    {usedProducts.map((up, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-xs bg-background rounded border border-border/40 px-2 py-1.5">
                        <div className="flex-1 min-w-0">
                          <span className="font-medium truncate block">{up.name}</span>
                          <span className={`text-xs ${up.usageType === "internal_service" ? "text-amber-600" : "text-blue-600"}`}>
                            {up.usageType === "internal_service" ? "✂️ Uso Interno" : "👤 Uso Próprio"} — R$ {up.unitCost.toFixed(2)}/un
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <button className="w-5 h-5 rounded border flex items-center justify-center hover:bg-secondary" onClick={() => setUsedProducts(prev => prev.map((u, i) => i === idx ? { ...u, qty: Math.max(0.5, parseFloat((u.qty - 0.5).toFixed(3))) } : u))}>
                            -
                          </button>
                          <span className="w-8 text-center font-medium">{up.qty}</span>
                          <button className="w-5 h-5 rounded border flex items-center justify-center hover:bg-secondary" onClick={() => setUsedProducts(prev => prev.map((u, i) => i === idx ? { ...u, qty: parseFloat((u.qty + 0.5).toFixed(3)) } : u))}>
                            +
                          </button>
                        </div>
                        <span className="w-16 text-right font-semibold text-amber-600">-R$ {(up.qty * up.unitCost).toFixed(2)}</span>
                        <button className="text-destructive hover:text-destructive/80" onClick={() => setUsedProducts(prev => prev.filter((_, i) => i !== idx))}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    <div className="flex justify-between text-xs font-semibold border-t border-amber-500/20 pt-1 mt-1 text-amber-700 dark:text-amber-400">
                      <span>Total descontado da comissão</span>
                      <span>-R$ {usedProductsDeductTotal.toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Assistente no atendimento */}
              <div className="border border-blue-500/30 rounded-lg p-3 space-y-2 bg-blue-500/5">
                <p className="text-sm font-medium flex items-center gap-2 text-blue-700 dark:text-blue-400">
                  <Users className="w-4 h-4" />
                  Assistente no Atendimento
                  <span className="text-xs font-normal text-muted-foreground ml-1">(abate % da comissão)</span>
                </p>
                <Select
                  value={assistantId ? String(assistantId) : "none"}
                  onValueChange={(v) => setAssistantId(v === "none" ? null : Number(v))}
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="Sem assistente" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sem assistente</SelectItem>
                    {(allProfessionals as any[]).filter((p: any) => p.id !== selectedAppt?.professionalId).map((p: any) => (
                      <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {assistantId && (
                  <p className="text-xs text-blue-600 dark:text-blue-400">
                    A comissão do assistente será calculada conforme a regra de comissão cadastrada para ele.
                  </p>
                )}
              </div>

              {/* Botão imprimir comanda */}
              <Button variant="outline" size="sm" className="w-full" onClick={() => printComanda(selectedAppt, undefined, commandaServices)}>
                <FileText className="w-4 h-4 mr-2" />
                Imprimir Comanda
              </Button>

              {/* Desconto com PIN */}
              <div className="border border-border/50 rounded-lg p-3 space-y-2">
                <p className="text-sm font-medium flex items-center gap-2">
                  <Percent className="w-4 h-4 text-primary" />
                  Desconto (requer autorização PIN)
                </p>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Label className="text-xs text-muted-foreground">Valor do desconto (R$)</Label>
                    <Input
                      type="number"
                      min="0"
                      max={totalPrice}
                      step="0.01"
                      value={discountAmount}
                      onChange={(e) => setDiscountAmount(e.target.value)}
                      placeholder="0,00"
                      disabled={discountApproved}
                      className="h-8 text-sm mt-1"
                    />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs text-muted-foreground">Motivo</Label>
                    <Input
                      value={discountReason}
                      onChange={(e) => setDiscountReason(e.target.value)}
                      placeholder="Ex: cliente fidelidade"
                      disabled={discountApproved}
                      className="h-8 text-sm mt-1"
                    />
                  </div>
                </div>
                {!discountApproved ? (
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full text-xs"
                    onClick={() => {
                      const val = Number(discountAmount);
                      if (!val || val <= 0) { toast.error("Informe um valor de desconto válido."); return; }
                      if (val > totalPrice) { toast.error("Desconto não pode ser maior que o total."); return; }
                      setPendingDiscount({ amount: discountAmount, reason: discountReason });
                      setDiscountPinOpen(true);
                    }}
                  >
                    <ShieldAlert className="w-3.5 h-3.5 mr-1" />
                    Solicitar Autorização de Desconto
                  </Button>
                ) : (
                  <div className="flex items-center gap-2 text-xs text-green-600 bg-green-50 rounded p-2">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Desconto de R$ {discountVal.toFixed(2)} autorizado
                  </div>
                )}
              </div>

              {/* Formas de pagamento */}
              <div className="space-y-2">
                <p className="text-sm font-medium">Formas de Pagamento</p>
                {splits.map((split, idx) => (
                  <div key={idx} className="flex gap-2 items-end">
                    <div className="flex-1">
                      {idx === 0 && <Label className="text-xs text-muted-foreground">Forma</Label>}
                      <div className="grid grid-cols-3 gap-1 mt-1">
                        {paymentMethods.map((method: any) => (
                          <button
                            key={method.value}
                            onClick={() => handleSplitChange(idx, "method", method.value)}
                            className={`flex min-h-12 flex-col items-center justify-center gap-0.5 rounded-lg border-2 p-2 text-xs font-medium transition-all sm:min-h-0 ${
                              split.method === method.value
                                ? "border-primary bg-primary/10 text-primary"
                                : `border-transparent ${method.color}`
                            }`}
                          >
                            <span className="w-4 h-4">{method.icon}</span>
                            {method.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="w-28">
                      {idx === 0 && <Label className="text-xs text-muted-foreground">Valor (R$)</Label>}
                      <Input
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="0.01"
                        value={split.amount}
                        onChange={(e) => handleSplitChange(idx, "amount", e.target.value)}
                        placeholder="0,00"
                        className="mt-1 h-11 text-sm sm:h-8"
                      />
                    </div>
                    {split.method === "credito" && (
                      <div className="w-24">
                        {idx === 0 && <Label className="text-xs text-muted-foreground">Parcelas</Label>}
                        <Select value={String(split.installments || 1)} onValueChange={(value) => handleSplitChange(idx, "installments", value)}>
                          <SelectTrigger className="mt-1 h-11 text-xs sm:h-8"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {Array.from({ length: 12 }, (_, index) => index + 1).map((installments) => (
                              <SelectItem key={installments} value={String(installments)}>{installments}x</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {splits.length > 1 && (
                      <Button size="sm" variant="ghost" className="mb-0.5 h-11 w-11 p-0 text-destructive sm:h-8 sm:w-8"
                        onClick={() => handleRemoveSplit(idx)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                ))}

                <Button size="sm" variant="outline" className="w-full text-xs" onClick={handleAddSplit}>
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  Adicionar outra forma de pagamento
                </Button>

                {/* Troco automático para Dinheiro */}
                {hasSingleCashSplit && (
                  <div className="border border-green-200 bg-green-50 rounded-lg p-3 space-y-2">
                    <p className="text-xs font-medium text-green-800 flex items-center gap-1">
                      <Banknote className="w-3.5 h-3.5" />
                      Troco automático
                    </p>
                    <div className="flex gap-3 items-end">
                      <div className="flex-1">
                        <Label className="text-xs text-muted-foreground">Valor recebido (R$)</Label>
                        <Input
                          type="number"
                          min={finalTotal}
                          step="0.01"
                          value={cashReceived}
                          onChange={(e) => setCashReceived(e.target.value)}
                          placeholder={finalTotal.toFixed(2)}
                          className="h-8 text-sm mt-1 border-green-300 focus:border-green-500"
                        />
                      </div>
                      <div className="flex-1">
                        <Label className="text-xs text-muted-foreground">Troco</Label>
                        <div className={`h-8 mt-1 rounded-md border px-3 flex items-center text-sm font-semibold ${
                          troco > 0 ? "bg-green-100 border-green-300 text-green-800" : "bg-gray-50 border-gray-200 text-gray-400"
                        }`}>
                          R$ {troco.toFixed(2)}
                        </div>
                      </div>
                    </div>
                    {cashReceivedVal > 0 && cashReceivedVal < finalTotal && (
                      <p className="text-xs text-red-600">⚠ Valor recebido menor que o total a pagar.</p>
                    )}
                  </div>
                )}

                {/* Resumo dos splits */}
                <div className={`rounded-lg p-3 text-sm flex justify-between items-center ${isBalanced ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`}>
                  <span>Total informado: <strong>R$ {totalSplits.toFixed(2)}</strong></span>
                  {!isBalanced && <span className="text-xs">Faltam R$ {remaining.toFixed(2)}</span>}
                  {isBalanced && <CheckCircle2 className="w-4 h-4" />}
                </div>
              </div>

              {(selectedAppt as any).clientPhone && (
                <div className="flex items-center gap-2 text-xs text-green-600 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                  <MessageCircle className="w-3.5 h-3.5 shrink-0" />
                  Mensagem de confirmação será enviada via WhatsApp ao confirmar.
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button
              onClick={openPaymentReview}
              disabled={!isBalanced || registerPaymentSplit.isPending}
              className="bg-primary text-primary-foreground"
            >
              {registerPaymentSplit.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Receipt className="w-4 h-4 mr-2" />
              )}
              Revisar antes de confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={paymentReviewOpen} onOpenChange={(open) => !open && setPaymentReviewOpen(false)}>
        <DialogContent className="max-h-[92dvh] max-w-lg overflow-y-auto p-5 sm:p-6">
          {selectedAppt && (
            <div className="space-y-5">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Receipt className="h-5 w-5 text-primary" />
                  Conferência final do pagamento
                </DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground">
                Revise os dados abaixo. O pagamento será registrado somente após a confirmação final.
              </p>

              <div className="space-y-3 rounded-xl border bg-muted/30 p-3 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">Cliente</span>
                  <span className="text-right font-semibold">{(selectedAppt as any).clientName ?? (selectedAppt as any).userName ?? "Cliente"}</span>
                </div>
                <div className="border-t pt-3">
                  <p className="mb-2 font-medium">Serviços e produtos</p>
                  <div className="space-y-1.5 text-muted-foreground">
                    {commandaServices.length > 0 ? commandaServices.map((service: any, index: number) => (
                      <div key={`${service.serviceName}-${index}`} className="flex justify-between gap-3">
                        <span className="min-w-0">{service.serviceName}</span>
                        <span className="shrink-0">R$ {Number(service.price).toFixed(2).replace(".", ",")}</span>
                      </div>
                    )) : (
                      <div className="flex justify-between gap-3">
                        <span>{(selectedAppt as any).serviceName ?? "Serviço"}</span>
                        <span>R$ {effectiveServicePrice.toFixed(2).replace(".", ",")}</span>
                      </div>
                    )}
                    {checkoutProducts.map((product) => (
                      <div key={product.productId} className="flex justify-between gap-3">
                        <span className="min-w-0">{product.quantity}x {product.name}</span>
                        <span className="shrink-0">R$ {(product.quantity * product.unitPrice).toFixed(2).replace(".", ",")}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="space-y-1 border-t pt-3">
                  <div className="flex justify-between gap-3 text-muted-foreground"><span>Subtotal</span><span>R$ {totalPrice.toFixed(2).replace(".", ",")}</span></div>
                  {discountVal > 0 && <div className="flex justify-between gap-3 text-red-700"><span>Desconto</span><span>- R$ {discountVal.toFixed(2).replace(".", ",")}</span></div>}
                  <div className="flex justify-between gap-3 pt-1 text-base font-bold text-primary"><span>Total</span><span>R$ {finalTotal.toFixed(2).replace(".", ",")}</span></div>
                </div>
              </div>

              <div className="rounded-xl border p-3 text-sm">
                <p className="mb-2 font-medium">Formas de pagamento</p>
                <div className="space-y-1.5">
                  {splits.map((split, index) => (
                    <div key={`${split.method}-${index}`} className="flex justify-between gap-3 text-muted-foreground">
                      <span>{methodLabels[split.method]}{split.method === "credito" && split.installments && split.installments > 1 ? ` · ${split.installments}x` : ""}</span>
                      <span className="font-medium text-foreground">R$ {Number(split.amount).toFixed(2).replace(".", ",")}</span>
                    </div>
                  ))}
                  {troco > 0 && <div className="flex justify-between gap-3 border-t pt-2 font-medium text-emerald-700"><span>Troco</span><span>R$ {troco.toFixed(2).replace(".", ",")}</span></div>}
                </div>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" disabled={registerPaymentSplit.isPending} onClick={() => setPaymentReviewOpen(false)}>
                  Voltar e corrigir
                </Button>
                <Button className="flex-1 gap-1" disabled={registerPaymentSplit.isPending} onClick={submitConfirmedPayment}>
                  {registerPaymentSplit.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  {registerPaymentSplit.isPending ? "Registrando..." : "Confirmar pagamento"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!paymentReceipt} onOpenChange={(open) => !open && setPaymentReceipt(null)}>
        <DialogContent className="max-w-md p-5 sm:p-6">
          {paymentReceipt && (
            <div className="space-y-5">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-emerald-800">
                  <CheckCircle2 className="h-5 w-5" />
                  Pagamento registrado
                </DialogTitle>
              </DialogHeader>
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
                <p className="font-semibold">R$ {paymentReceipt.totalPaid.toFixed(2).replace(".", ",")}</p>
                <p className="mt-1">O comprovante abre em uma nova janela. Se ele não aparecer, use “Reimprimir comprovante”.</p>
              </div>
              <div className="space-y-2">
                <Button className="h-11 w-full gap-2" onClick={() => {
                  const opened = printCheckoutNote(paymentReceipt);
                  if (!opened) {
                    toast.error("O navegador bloqueou a nova janela. Libere pop-ups e tente reimprimir.");
                    return;
                  }
                  setReceiptPrintAttempted(true);
                }}>
                  <Printer className="h-4 w-4" />
                  {receiptPrintAttempted ? "Reimprimir comprovante" : "Abrir comprovante"}
                </Button>
                <Button variant="outline" className="w-full" onClick={() => {
                  setPaymentReceipt(null);
                  if (cameFromAgenda) navigate("/weekly-calendar");
                }}>
                  {receiptPrintAttempted ? "Comprovante abriu — concluir" : "Concluir sem comprovante"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={saleReviewOpen} onOpenChange={(open) => !open && !sellProductsMutation.isPending && setSaleReviewOpen(false)}>
        <DialogContent className="max-h-[92dvh] max-w-lg overflow-y-auto p-5 sm:p-6">
          <div className="space-y-5">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5 text-primary" />
                {isProfessionalConsumption ? "Conferência do consumo" : "Conferência final da venda"}
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              {isProfessionalConsumption
                ? "Revise os produtos. O estoque será abatido e o valor será lançado na comissão somente após confirmar."
                : "Revise os dados abaixo. A venda só será registrada depois da confirmação final."}
            </p>
            <div className="space-y-3 rounded-xl border bg-muted/30 p-3 text-sm">
              {isProfessionalConsumption ? (
                <div className="flex justify-between gap-3"><span className="text-muted-foreground">Profissional</span><span className="font-semibold">{saleProfessional?.name}</span></div>
              ) : saleClientName ? (
                <div className="flex justify-between gap-3"><span className="text-muted-foreground">Cliente</span><span className="font-semibold">{saleClientName}</span></div>
              ) : null}
              <div className="space-y-1.5 border-t pt-3 text-muted-foreground">
                {saleProducts.map((product) => (
                  <div key={product.productId} className="flex justify-between gap-3">
                    <span className="min-w-0">{product.quantity}x {product.name}</span>
                    <span className="shrink-0">R$ {(product.quantity * product.unitPrice).toFixed(2).replace(".", ",")}</span>
                  </div>
                ))}
              </div>
              {isProfessionalConsumption ? (
                <div className="flex justify-between border-t pt-3 text-base font-bold text-amber-800"><span>Débito na comissão</span><span>- R$ {saleProfessionalConsumptionTotal.toFixed(2).replace(".", ",")}</span></div>
              ) : (
                <div className="space-y-1 border-t pt-3">
                  <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span>R$ {saleTotalProducts.toFixed(2).replace(".", ",")}</span></div>
                  {saleDiscountVal > 0 && <div className="flex justify-between text-red-700"><span>Desconto</span><span>- R$ {saleDiscountVal.toFixed(2).replace(".", ",")}</span></div>}
                  <div className="flex justify-between pt-1 text-base font-bold text-primary"><span>Total</span><span>R$ {saleFinalTotal.toFixed(2).replace(".", ",")}</span></div>
                </div>
              )}
            </div>
            {!isProfessionalConsumption && (
              <div className="rounded-xl border p-3 text-sm">
                <p className="mb-2 font-medium">Formas de pagamento</p>
                <div className="space-y-1.5">
                  {saleSplits.map((split, index) => (
                    <div className="flex justify-between gap-3 text-muted-foreground" key={`${split.method}-${index}`}>
                      <span>{methodLabels[split.method]}</span>
                      <span className="font-medium text-foreground">R$ {Number(split.amount).toFixed(2).replace(".", ",")}</span>
                    </div>
                  ))}
                  {saleTroco > 0 && <div className="flex justify-between border-t pt-2 font-medium text-emerald-700"><span>Troco</span><span>R$ {saleTroco.toFixed(2).replace(".", ",")}</span></div>}
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" disabled={sellProductsMutation.isPending} onClick={() => setSaleReviewOpen(false)}>
                Voltar e editar
              </Button>
              <Button className="flex-1 gap-1" disabled={sellProductsMutation.isPending} onClick={submitSale}>
                {sellProductsMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                {sellProductsMutation.isPending ? "Registrando..." : isProfessionalConsumption ? "Confirmar consumo" : "Confirmar venda"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!saleReceipt} onOpenChange={(open) => !open && setSaleReceipt(null)}>
        <DialogContent className="max-w-md p-5 sm:p-6">
          {saleReceipt && (
            <div className="space-y-5">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-emerald-800">
                  <CheckCircle2 className="h-5 w-5" />
                  Venda registrada
                </DialogTitle>
              </DialogHeader>
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
                <p className="font-semibold">R$ {saleReceipt.totalPaid.toFixed(2).replace(".", ",")}</p>
                <p className="mt-1">Abra o comprovante agora. Se a janela não aparecer, use a opção de reimpressão.</p>
              </div>
              <div className="space-y-2">
                <Button className="h-11 w-full gap-2" onClick={() => {
                  const opened = printStandaloneSaleReceipt(saleReceipt);
                  if (!opened) {
                    toast.error("O navegador bloqueou a nova janela. Libere pop-ups e tente reimprimir.");
                    return;
                  }
                  setSaleReceiptPrintAttempted(true);
                }}>
                  <Printer className="h-4 w-4" />
                  {saleReceiptPrintAttempted ? "Reimprimir comprovante" : "Abrir comprovante"}
                </Button>
                <Button variant="outline" className="w-full" onClick={() => setSaleReceipt(null)}>
                  {saleReceiptPrintAttempted ? "Comprovante abriu — concluir" : "Concluir sem comprovante"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
